/**
 * Types et constantes pour la gestion des métiers dans la prise de besoin TI
 * Structure versionnée et extensible avec questions holistiques spécifiques à chaque métier
 * Basé sur les meilleures pratiques sectorielles et les besoins TI réels
 */

export type MetierBesoin = {
  metierId: string; // ex: "avocats", "comptables", "ingenieurs_civil"
  version: string; // ex: "1.0.0" pour versionner la structure
  // Champs génériques (optionnels selon métier)
  resume_activites?: string;
  processus_cles?: string[];
  logiciels_métier?: string[];
  contraintes_reglementaires?: string;
  enjeux_principaux?: string[];
  donnees_critiques?: string[];
  dependances_ti?: string[];
  temps_arret_max?: string;
  remarques?: string;
  // Champs spécifiques par métier (définis dynamiquement dans les formulaires)
  [key: string]: any;
};

export type FieldType = 'text' | 'textarea' | 'multiline_list' | 'select' | 'multiselect';

export type MetierField = {
  id: string; // Peut être une clé de MetierBesoin ou un champ spécifique
  label: string;
  type: FieldType;
  placeholder?: string;
  helpText?: string;
  options?: string[]; // pour les selects et multiselects
  required?: boolean;
  maxSelections?: number; // pour multiselect
};

export type MetierFormConfig = {
  metierId: string;
  titre: string;
  description: string;
  fields: MetierField[];
};

// Liste officielle des métiers (subdivisés en catégories spécifiques)
export const METIERS = [
  // Services professionnels - Détail
  { id: 'avocats', label: 'Avocats / Cabinet juridique' },
  { id: 'comptables', label: 'Comptables / CPA' },
  { id: 'notaires', label: 'Notaires' },
  { id: 'huissier', label: 'Huissier' },
  { id: 'ingenieurs_civil', label: 'Ingénieurs (Génie civil)' },
  { id: 'ingenieurs_mecanique', label: 'Ingénieurs (Mécanique / Industriel)' },
  { id: 'ingenieurs_electrique', label: 'Ingénieurs (Électrique / Automatisation)' },
  { id: 'architectes', label: 'Architectes' },
  { id: 'consultants', label: 'Consultants (Général)' },
  { id: 'conseillers_financiers', label: 'Conseillers financiers / Planificateurs' },
  
  // Construction & Immobilier
  { id: 'construction_residentielle', label: 'Construction résidentielle' },
  { id: 'construction_commerciale', label: 'Construction commerciale / industrielle' },
  { id: 'gestionnaire_projets_construction', label: 'Gestionnaire de projets de construction' },
  { id: 'gestion_immobiliere', label: 'Gestion immobilière / Syndicats' },
  { id: 'evaluation_projets_immobilier', label: 'Firme d\'évaluation de projets Immobilier' },
  { id: 'renovation', label: 'Rénovation / Rénovation écologique' },
  
  // Santé
  { id: 'cliniques_medecine', label: 'Cliniques de médecine familiale' },
  { id: 'cliniques_specialisees', label: 'Cliniques spécialisées' },
  { id: 'hopitaux', label: 'Hôpitaux / Centres hospitaliers' },
  { id: 'pharmacies', label: 'Pharmacies' },
  { id: 'dentistes', label: 'Dentistes / Cliniques dentaires' },
  { id: 'optometristes', label: 'Optométristes' },
  
  // Manufacturier & Industriel
  { id: 'manufacturier_alimentaire', label: 'Manufacturier alimentaire' },
  { id: 'transformation_distribution_peche', label: 'Transformation & distribution (pêche)' },
  { id: 'manufacturier_mecanique', label: 'Manufacturier mécanique / Métallurgie' },
  { id: 'manufacturier_plastique', label: 'Manufacturier plastique / Composite' },
  { id: 'manufacturier_textile', label: 'Manufacturier textile / Vêtements' },
  { id: 'manufacturier_bois', label: 'Manufacturier bois / Meubles' },
  
  // Transport & Logistique
  { id: 'transport_routier', label: 'Transport routier / Camionnage' },
  { id: 'transport_express', label: 'Messagerie / Transport express' },
  { id: 'entreposage', label: 'Entreposage / Entrepôts' },
  { id: 'logistique', label: 'Logistique / Supply chain' },
  
  // Commerce & Détail
  { id: 'commerce_detail', label: 'Commerce de détail (Général)' },
  { id: 'commerce_alimentaire', label: 'Commerce alimentaire / Épiceries' },
  { id: 'commerce_specialise', label: 'Commerce spécialisé' },
  { id: 'ecommerce', label: 'E-commerce / Vente en ligne' },
  
  // Technologie & SaaS
  { id: 'developpement_logiciel', label: 'Développement logiciel' },
  { id: 'saas', label: 'SaaS / Services cloud' },
  { id: 'cybersecurite', label: 'Cybersécurité / Services sécurité' },
  { id: 'telecommunications', label: 'Télécommunications' },
  
  // Éducation & OBNL
  { id: 'ecoles_privees', label: 'Écoles privées' },
  { id: 'organismes_formation', label: 'Organismes de formation' },
  { id: 'agence_education_medicale', label: 'Agence en éducation médicale' },
  { id: 'obnl', label: 'OBNL / Organismes communautaires' },
  { id: 'syndicats', label: 'Syndicats / Associations' },
  
  // Restauration & Hôtellerie
  { id: 'restaurants', label: 'Restaurants' },
  { id: 'hotels', label: 'Hôtels / Hébergement' },
  { id: 'catering', label: 'Traiteurs / Catering' },
  
  // Automobile
  { id: 'concessionnaires', label: 'Concessionnaires automobiles' },
  { id: 'garages', label: 'Garages / Réparation automobile' },
  { id: 'pieces_automobile', label: 'Pièces automobiles / Accessoires' },
  
  // Services financiers
  { id: 'banques', label: 'Banques / Institutions bancaires' },
  { id: 'assurances', label: 'Assurances' },
  { id: 'courtage', label: 'Courtage / Investissement' },
  { id: 'gestion_patrimoine', label: 'Gestion de patrimoine' },
  
  // Services spécialisés
  { id: 'centre_funeraire', label: 'Centre funéraire / Accompagnement des familles endeuillées' },
  { id: 'amenagement_paysager', label: 'Aménagement paysager / Entretien de pelouses' },
] as const;

// Version actuelle de la structure MetierBesoin
export const METIER_BESOIN_VERSION = '1.0.0';

// Helper pour créer un MetierBesoin vide
export function createEmptyMetierBesoin(metierId: string): MetierBesoin {
  return {
    metierId,
    version: METIER_BESOIN_VERSION,
  };
}

// Helper pour obtenir la configuration d'un métier
export function getMetierFormConfig(metierId: string): MetierFormConfig | undefined {
  return METIER_FORMS.find((config) => config.metierId === metierId);
}

// ============================================================================
// CONFIGURATIONS DES FORMULAIRES PAR MÉTIER
// Questions holistiques basées sur les besoins TI réels de chaque secteur
// ============================================================================

export const METIER_FORMS: MetierFormConfig[] = [
  // ========== AVOCATS / CABINET JURIDIQUE ==========
  {
    metierId: 'avocats',
    titre: 'Besoins métier – Cabinet d\'avocats',
    description: 'Comprendre les réalités des cabinets juridiques : gestion de dossiers, facturation au temps, confidentialité, conformité déontologique.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de services juridiques offerts',
        type: 'multiselect',
        options: [
          'Droit des affaires / Commercial',
          'Litige civil',
          'Droit de la famille',
          'Droit immobilier',
          'Droit du travail',
          'Droit fiscal',
          'Droit pénal',
          'Droit administratif',
          'Droit de l\'immigration',
          'Propriété intellectuelle',
          'Autre',
        ],
        required: true,
        helpText: 'Sélectionnez tous les domaines de pratique du cabinet',
      },
      {
        id: 'nombre_avocats',
        label: 'Nombre d\'avocats dans le cabinet',
        type: 'select',
        options: ['1-5 avocats', '6-15 avocats', '16-50 avocats', '50+ avocats'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Gestion des dossiers clients',
          'Facturation au temps (billable hours)',
          'Suivi des échéances / Prescriptions',
          'Gestion des conflits d\'intérêts',
          'Préparation d\'audiences / Plaidoyers',
          'Recherche juridique',
          'Rédaction de contrats',
          'Négociations',
          'Gestion des pièces justificatives',
          'Communication avec clients',
          'Gestion des rendez-vous',
        ],
        required: true,
        helpText: 'Sélectionnez les processus les plus importants pour votre cabinet',
      },
      {
        id: 'logiciels_métier',
        label: 'Logiciels métier utilisés actuellement',
        type: 'multiselect',
        options: [
          'Aucun logiciel spécialisé',
          'Clio / TimeSolv / Bill4Time (gestion + facturation)',
          'Worldox / iManage (gestion documentaire)',
          'LexisNexis / Westlaw (recherche juridique)',
          'DocuSign / Adobe Sign (signature électronique)',
          'Outlook / Gmail (courriel)',
          'Microsoft 365 / Google Workspace',
          'Logiciel comptable (QuickBooks, Sage, etc.)',
          'Autre logiciel spécialisé',
        ],
        required: false,
      },
      {
        id: 'gestion_dossiers',
        label: 'Comment gérez-vous actuellement vos dossiers clients ?',
        type: 'select',
        options: [
          'Dossiers papier uniquement',
          'Dossiers papier + fichiers numériques (non organisés)',
          'Dossiers numériques organisés (dossiers Windows/partage réseau)',
          'Logiciel de gestion de dossiers dédié',
          'Système hybride (papier + numérique organisé)',
        ],
        required: true,
      },
      {
        id: 'facturation_temps',
        label: 'Méthode de facturation actuelle',
        type: 'select',
        options: [
          'Facturation au temps (heures facturables)',
          'Forfait / Prix fixe par dossier',
          'Mixte (temps + forfait)',
          'Autre modèle',
        ],
        required: true,
      },
      {
        id: 'suivi_temps',
        label: 'Comment suivez-vous le temps passé sur les dossiers ?',
        type: 'select',
        options: [
          'Manuellement (notes, feuilles de temps)',
          'Application mobile simple',
          'Logiciel de suivi de temps intégré',
          'Nous ne suivons pas systématiquement',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / déontologie',
        type: 'multiselect',
        options: [
          'Barreau du Québec (règles déontologiques)',
          'Confidentialité client-avocat (secret professionnel)',
          'Conservation des dossiers (7-10 ans)',
          'Gestion des conflits d\'intérêts',
          'Facturation transparente',
          'Protection des données personnelles (Loi 25)',
          'Conformité anti-blanchiment (si applicable)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques pour le cabinet',
        type: 'multiselect',
        options: [
          'Dossiers clients complets',
          'Pièces justificatives / Preuves',
          'Correspondances clients',
          'Notes de recherche juridique',
          'Historique des facturations',
          'Calendrier des échéances',
          'Base de connaissances juridique interne',
          'Templates de contrats / Documents types',
        ],
        required: true,
      },
      {
        id: 'travail_distance',
        label: 'Travail à distance / Hybride',
        type: 'select',
        options: [
          '100% en présentiel',
          'Hybride (2-3 jours/semaine à distance)',
          'Principalement à distance',
          'Flexible selon les besoins',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Accès aux dossiers à distance (24/7)',
          'Courriel professionnel fiable',
          'Calendrier partagé / Gestion rendez-vous',
          'Téléphonie IP / Visioconférence',
          'Accès sécurisé aux bases de données juridiques',
          'Partage de fichiers volumineux (pièces justificatives)',
          'Sauvegardes automatiques fréquentes',
          'Signature électronique de documents',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['15 minutes', '30 minutes', '2 heures', '1 jour'],
        required: true,
        helpText: 'Au-delà de ce délai, les opérations sont fortement impactées',
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Perte de temps à chercher l\'information dans les dossiers',
          'Doublons de documents / Manque d\'organisation',
          'Difficulté de travail à distance',
          'Sécurité des données clients insuffisante',
          'Facturation incomplète / Temps non facturé',
          'Gestion des échéances / Risque de prescription',
          'Communication inefficace entre avocats',
          'Manque de visibilité sur la rentabilité des dossiers',
          'Conformité déontologique / Risques réglementaires',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        placeholder: 'Ex: Projet de croissance, fusion avec autre cabinet, modernisation en cours...',
        required: false,
      },
    ],
  },

  // ========== COMPTABLES / CPA ==========
  {
    metierId: 'comptables',
    titre: 'Besoins métier – Cabinet comptable / CPA',
    description: 'Comprendre les réalités des cabinets comptables : déclarations fiscales, tenue de livres, conformité, saisonnalité.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de services comptables offerts',
        type: 'multiselect',
        options: [
          'Tenue de livres comptables',
          'Préparation déclarations fiscales (personnelles)',
          'Préparation déclarations fiscales (entreprises)',
          'Vérification / Audit',
          'Conseil fiscal',
          'Planification financière',
          'Paye / Paie',
          'Gestion de la paie',
          'Conseil en gestion',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_comptables',
        label: 'Nombre de comptables / employés',
        type: 'select',
        options: ['1-3 personnes', '4-10 personnes', '11-25 personnes', '25+ personnes'],
        required: true,
      },
      {
        id: 'nombre_clients',
        label: 'Nombre approximatif de clients',
        type: 'select',
        options: ['Moins de 50 clients', '50-150 clients', '150-500 clients', '500+ clients'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Collecte documents clients (relevés, factures, etc.)',
          'Saisie comptable / Tenue de livres',
          'Rapprochements bancaires',
          'Préparation déclarations fiscales',
          'Transmission électronique déclarations (NETFILE)',
          'Gestion de la paie clients',
          'Production d\'états financiers',
          'Vérification / Audit',
          'Conseil fiscal / Optimisation',
          'Communication avec clients',
          'Gestion des échéances fiscales',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Logiciels comptables utilisés',
        type: 'multiselect',
        options: [
          'Sage 50 / Simply Accounting',
          'QuickBooks',
          'Acomba',
          'Dynacom',
          'CaseWare',
          'Excel (tenue de livres)',
          'Logiciel de paie (ADP, Ceridian, etc.)',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'collecte_documents',
        label: 'Comment collectez-vous les documents clients ?',
        type: 'select',
        options: [
          'Papier uniquement (courrier, rendez-vous)',
          'Courriel (pièces jointes)',
          'Portail client sécurisé',
          'Application mobile',
          'Mixte (papier + numérique)',
        ],
        required: true,
      },
      {
        id: 'saisonnalite',
        label: 'Période de pointe (saisonnalité)',
        type: 'multiselect',
        options: [
          'Janvier-Mars (déclarations personnelles)',
          'Avril-Juin (déclarations entreprises)',
          'Juillet-Août (moins chargé)',
          'Septembre-Décembre (planification)',
          'Charge relativement constante',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Ordre des comptables professionnels agréés (CPA)',
          'Loi sur les comptables professionnels',
          'Loi de l\'impôt sur le revenu',
          'Loi sur la taxe de vente du Québec (TVQ)',
          'Loi sur les taxes à la consommation (TPS)',
          'Protection des données personnelles (Loi 25)',
          'Conservation des dossiers (7 ans minimum)',
          'Confidentialité client-comptable',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Dossiers clients complets',
          'Relevés bancaires / Transactions',
          'Factures / Reçus clients',
          'Déclarations fiscales historiques',
          'États financiers',
          'Fichiers de paie',
          'Correspondances avec Revenu Québec / ARC',
          'Base de connaissances fiscale',
        ],
        required: true,
      },
      {
        id: 'travail_distance',
        label: 'Travail à distance / Hybride',
        type: 'select',
        options: [
          '100% en présentiel',
          'Hybride (surtout en période creuse)',
          'Principalement à distance',
          'Flexible selon la période',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Accès aux logiciels comptables (24/7 en période de pointe)',
          'Connexion sécurisée aux serveurs Revenu Québec / ARC',
          'Portail client pour collecte documents',
          'Sauvegardes automatiques fréquentes',
          'Partage de fichiers avec clients',
          'Courriel professionnel fiable',
          'Téléphonie IP / Visioconférence',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['30 minutes', '2 heures', '1 jour', '3 jours (hors période de pointe)'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Collecte documents clients inefficace',
          'Saisie comptable manuelle / Répétitive',
          'Gestion de la charge de travail en période de pointe',
          'Sécurité des données fiscales insuffisante',
          'Communication avec clients difficile',
          'Manque de visibilité sur la rentabilité par client',
          'Conformité fiscale / Risques d\'erreurs',
          'Modernisation des processus',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== INGÉNIEURS (GÉNIE CIVIL) ==========
  {
    metierId: 'ingenieurs_civil',
    titre: 'Besoins métier – Ingénieurs (Génie civil)',
    description: 'Comprendre les réalités des bureaux d\'ingénierie : projets d\'infrastructure, CAO, gestion de projets, conformité.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de projets / services',
        type: 'multiselect',
        options: [
          'Infrastructure routière',
          'Ponts et structures',
          'Gestion de l\'eau (aqueduc, égouts)',
          'Génie municipal',
          'Génie géotechnique',
          'Génie environnemental',
          'Projets résidentiels',
          'Projets commerciaux / industriels',
          'Inspection / Expertise',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_ingenieurs',
        label: 'Nombre d\'ingénieurs / employés',
        type: 'select',
        options: ['1-5 personnes', '6-15 personnes', '16-50 personnes', '50+ personnes'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Conception technique (CAO)',
          'Calculs structuraux',
          'Plans et devis',
          'Gestion de projets',
          'Suivi de chantier',
          'Inspection / Contrôle qualité',
          'Rapports techniques',
          'Soumissions / Appels d\'offres',
          'Coordination avec architectes / entrepreneurs',
          'Gestion des permis',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Logiciels CAO / techniques utilisés',
        type: 'multiselect',
        options: [
          'AutoCAD',
          'Civil 3D',
          'MicroStation',
          'Revit (structures)',
          'STAAD.Pro / SAP2000 (calculs)',
          'Excel (calculs)',
          'Logiciels spécialisés (hydraulique, géotechnique, etc.)',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'gestion_projets',
        label: 'Comment gérez-vous vos projets ?',
        type: 'select',
        options: [
          'Excel / Outils basiques',
          'Microsoft Project',
          'Logiciel de gestion de projets dédié',
          'Système maison / Sur mesure',
          'Pas de système formel',
        ],
        required: true,
      },
      {
        id: 'fichiers_cao',
        label: 'Taille typique des fichiers CAO / techniques',
        type: 'select',
        options: [
          'Petits fichiers (< 10 MB)',
          'Fichiers moyens (10-50 MB)',
          'Gros fichiers (50-200 MB)',
          'Très gros fichiers (> 200 MB)',
        ],
        required: true,
      },
      {
        id: 'collaboration',
        label: 'Collaboration avec autres professionnels',
        type: 'multiselect',
        options: [
          'Architectes',
          'Autres ingénieurs (spécialités différentes)',
          'Entrepreneurs généraux',
          'Sous-traitants',
          'Clients / Promoteurs',
          'Municipalités / Gouvernements',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / normatives',
        type: 'multiselect',
        options: [
          'Ordre des ingénieurs du Québec (OIQ)',
          'Code de déontologie des ingénieurs',
          'Code du bâtiment',
          'Normes CSA / BNQ',
          'Règlements municipaux',
          'Environnement (MDDELCC)',
          'Sécurité sur les chantiers (CNESST)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Plans techniques (CAO)',
          'Calculs structuraux',
          'Rapports techniques',
          'Photos de chantier',
          'Correspondances projets',
          'Historique des projets',
          'Bibliothèque de détails types',
          'Spécifications techniques',
        ],
        required: true,
      },
      {
        id: 'travail_distance',
        label: 'Travail à distance / Hybride',
        type: 'select',
        options: [
          '100% en présentiel',
          'Hybride (bureau + chantier)',
          'Principalement à distance',
          'Flexible selon les projets',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Accès aux fichiers CAO volumineux',
          'Partage de fichiers avec collaborateurs',
          'Licences logiciels CAO (gestion)',
          'Sauvegardes automatiques fréquentes',
          'Accès à distance aux projets',
          'Intégration CAO / Gestion projets',
          'Impression grand format',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['2 heures', '1 jour', '3 jours'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Gestion de versions de plans / Conflits',
          'Partage fichiers volumineux inefficace',
          'Manque de visibilité sur l\'avancement projets',
          'Perte de temps à chercher l\'information',
          'Collaboration difficile avec autres professionnels',
          'Gestion des échéances projets',
          'Sécurité des données techniques',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== ARCHITECTES ==========
  {
    metierId: 'architectes',
    titre: 'Besoins métier – Architectes',
    description: 'Comprendre les réalités des bureaux d\'architecture : conception, CAO/BIM, maquettes, gestion de projets, collaboration.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de projets / services',
        type: 'multiselect',
        options: [
          'Résidentiel (maisons, condos)',
          'Commercial (bureaux, commerces)',
          'Institutionnel (écoles, hôpitaux)',
          'Industriel',
          'Rénovation / Restauration',
          'Urbanisme',
          'Design intérieur',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_architectes',
        label: 'Nombre d\'architectes / employés',
        type: 'select',
        options: ['1-3 personnes', '4-10 personnes', '11-25 personnes', '25+ personnes'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Conception architecturale',
          'Modélisation 3D / BIM',
          'Plans et devis',
          'Maquettes numériques / Rendu',
          'Gestion de projets',
          'Coordination avec ingénieurs',
          'Présentation clients',
          'Soumissions / Appels d\'offres',
          'Suivi de chantier',
          'Gestion des permis',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Logiciels CAO / BIM utilisés',
        type: 'multiselect',
        options: [
          'AutoCAD',
          'Revit (BIM)',
          'SketchUp',
          'Archicad',
          'Vectorworks',
          'Lumion / Enscape (rendu)',
          'Photoshop / Illustrator',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'utilisation_bim',
        label: 'Utilisez-vous le BIM (Building Information Modeling) ?',
        type: 'select',
        options: [
          'Oui, BIM complet (Revit, Archicad)',
          'Partiellement (certains projets)',
          'Non, CAO 2D uniquement (AutoCAD)',
          'En transition vers BIM',
        ],
        required: true,
      },
      {
        id: 'fichiers_3d',
        label: 'Taille typique des fichiers 3D / BIM',
        type: 'select',
        options: [
          'Petits fichiers (< 50 MB)',
          'Fichiers moyens (50-200 MB)',
          'Gros fichiers (200-500 MB)',
          'Très gros fichiers (> 500 MB)',
        ],
        required: true,
      },
      {
        id: 'collaboration',
        label: 'Collaboration avec autres professionnels',
        type: 'multiselect',
        options: [
          'Ingénieurs (structure, mécanique, électrique)',
          'Entrepreneurs généraux',
          'Sous-traitants',
          'Clients / Promoteurs',
          'Municipalités',
          'Designers intérieurs',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / normatives',
        type: 'multiselect',
        options: [
          'Ordre des architectes du Québec (OAQ)',
          'Code de déontologie des architectes',
          'Code du bâtiment',
          'Règlements municipaux (zonage)',
          'Normes d\'accessibilité',
          'Normes environnementales (LEED, etc.)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Modèles 3D / BIM',
          'Plans techniques',
          'Rendus / Visualisations',
          'Spécifications',
          'Bibliothèque de composants',
          'Historique des projets',
          'Correspondances projets',
        ],
        required: true,
      },
      {
        id: 'travail_distance',
        label: 'Travail à distance / Hybride',
        type: 'select',
        options: [
          '100% en présentiel',
          'Hybride (bureau + chantier)',
          'Principalement à distance',
          'Flexible selon les projets',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Accès aux fichiers 3D / BIM volumineux',
          'Rendu 3D (calculs intensifs)',
          'Partage de fichiers avec collaborateurs',
          'Licences logiciels CAO/BIM (gestion)',
          'Sauvegardes automatiques fréquentes',
          'Accès à distance aux projets',
          'Impression grand format',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['2 heures', '1 jour', '3 jours'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Gestion de versions BIM / Conflits',
          'Partage fichiers volumineux inefficace',
          'Rendu 3D lent / Ressources insuffisantes',
          'Manque de visibilité sur l\'avancement projets',
          'Collaboration difficile avec autres professionnels',
          'Gestion des échéances projets',
          'Sécurité des données techniques',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== NOTAIRES ==========
  {
    metierId: 'notaires',
    titre: 'Besoins métier – Notaires',
    description: 'Comprendre les réalités des études notariales : actes notariés, gestion de dossiers, conformité, conservation.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types d\'actes notariés / services',
        type: 'multiselect',
        options: [
          'Achat / Vente immobilière',
          'Hypothèques',
          'Testaments',
          'Donations',
          'Mariages / Contrats de mariage',
          'Successions',
          'Sociétés / Incorporation',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_notaires',
        label: 'Nombre de notaires / employés',
        type: 'select',
        options: ['1-3 personnes', '4-10 personnes', '11-25 personnes', '25+ personnes'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Préparation actes notariés',
          'Vérification titres / Recherche',
          'Signature actes',
          'Enregistrement actes',
          'Gestion de dossiers clients',
          'Communication avec clients',
          'Coordination avec autres professionnels',
          'Conservation des minutes',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'Logiciel notarial spécialisé',
          'Microsoft 365 / Google Workspace',
          'Gestion documentaire',
          'Recherche titres (Registre foncier)',
          'Autre',
        ],
        required: false,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / déontologie',
        type: 'multiselect',
        options: [
          'Chambre des notaires du Québec',
          'Conservation des minutes (permanente)',
          'Confidentialité client-notaire',
          'Protection des données personnelles (Loi 25)',
          'Enregistrement actes (obligatoire)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Minutes notariales',
          'Actes notariés',
          'Recherches titres',
          'Dossiers clients',
          'Correspondances',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['30 minutes', '2 heures', '1 jour'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Gestion de dossiers inefficace',
          'Conservation des minutes (espace)',
          'Recherche titres longue',
          'Communication avec clients',
          'Sécurité des données',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== HUISSIER ==========
  {
    metierId: 'huissier',
    titre: 'Besoins métier – Huissier',
    description: 'Comprendre les réalités des études d\'huissiers : signification, exécution, saisie, procédures, conformité et conservation des actes.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de services / activités',
        type: 'multiselect',
        options: [
          'Signification d\'actes judiciaires',
          'Exécution de jugements',
          'Saisie (préjudicielle, exécution)',
          'Constats (état des lieux, non-conformité)',
          'Recouvrement de créances',
          'Mise en demeure',
          'Autre',
        ],
        required: true,
        helpText: 'Sélectionnez les services offerts par l\'étude',
      },
      {
        id: 'nombre_personnes',
        label: 'Nombre d\'huissiers / employés',
        type: 'select',
        options: ['1-3 personnes', '4-10 personnes', '11-25 personnes', '25+ personnes'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Gestion des dossiers de signification',
          'Planification des constats',
          'Exécution des saisies',
          'Rédaction de procès-verbaux',
          'Suivi des échéances',
          'Communication avec avocats / tribunaux',
          'Conservation des actes',
          'Facturation / Recouvrement',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'Logiciel spécialisé huissier',
          'Microsoft 365 / Google Workspace',
          'Gestion documentaire',
          'Géolocalisation / Planification tournées',
          'Facturation / Comptabilité',
          'Autre',
        ],
        required: false,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / déontologie',
        type: 'multiselect',
        options: [
          'Chambre des huissiers du Québec',
          'Conservation des actes (obligatoire)',
          'Confidentialité',
          'Protection des données personnelles (Loi 25)',
          'Règles de signification (Code de procédure civile)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Procès-verbaux',
          'Actes de signification',
          'Dossiers clients',
          'Preuves de signification',
          'Correspondances / Mises en demeure',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['30 minutes', '2 heures', '1 jour'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Gestion de dossiers inefficace',
          'Planification des tournées',
          'Traçabilité des significations',
          'Sécurité des données',
          'Conformité réglementaire',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== INGÉNIEURS (MÉCANIQUE / INDUSTRIEL) ==========
  {
    metierId: 'ingenieurs_mecanique',
    titre: 'Besoins métier – Ingénieurs (Mécanique / Industriel)',
    description: 'Comprendre les réalités des bureaux d\'ingénierie mécanique : conception, CAO, simulation, fabrication.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de projets / services',
        type: 'multiselect',
        options: [
          'Conception mécanique',
          'Fabrication / Usinage',
          'Prototypage',
          'Simulation / Analyse',
          'Maintenance industrielle',
          'Automatisation',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_ingenieurs',
        label: 'Nombre d\'ingénieurs / employés',
        type: 'select',
        options: ['1-5 personnes', '6-15 personnes', '16-50 personnes', '50+ personnes'],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Logiciels CAO / techniques utilisés',
        type: 'multiselect',
        options: [
          'SolidWorks',
          'Inventor',
          'CATIA',
          'AutoCAD Mechanical',
          'ANSYS (simulation)',
          'Mastercam / CAM',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Conception 3D',
          'Simulation / Analyse',
          'Génération plans',
          'Programmation CNC',
          'Gestion de projets',
          'Prototypage',
        ],
        required: true,
      },
      {
        id: 'fichiers_cao',
        label: 'Taille typique des fichiers CAO',
        type: 'select',
        options: [
          'Petits fichiers (< 50 MB)',
          'Fichiers moyens (50-200 MB)',
          'Gros fichiers (200-500 MB)',
          'Assemblages très complexes (> 500 MB)',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires',
        type: 'multiselect',
        options: [
          'Ordre des ingénieurs du Québec (OIQ)',
          'Normes CSA',
          'Sécurité machines (CNESST)',
          'Environnement',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Modèles 3D CAO',
          'Plans techniques',
          'Programmes CNC',
          'Rapports simulation',
          'Spécifications',
        ],
        required: true,
      },
      {
        id: 'travail_distance',
        label: 'Travail à distance / Hybride',
        type: 'select',
        options: [
          '100% en présentiel',
          'Hybride (bureau + usine)',
          'Principalement à distance',
          'Flexible selon les projets',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Accès aux fichiers CAO volumineux',
          'Simulation (calculs intensifs)',
          'Partage de fichiers',
          'Licences logiciels CAO',
          'Sauvegardes automatiques',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['2 heures', '1 jour', '3 jours'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Gestion versions CAO',
          'Simulation lente',
          'Partage fichiers volumineux',
          'Intégration CAO-CAM',
          'Manque de visibilité projets',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== CONSTRUCTION RÉSIDENTIELLE ==========
  {
    metierId: 'construction_residentielle',
    titre: 'Besoins métier – Construction résidentielle',
    description: 'Comprendre les réalités des constructeurs résidentiels : gestion de chantiers, soumissions, suivi projets.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de projets',
        type: 'multiselect',
        options: [
          'Maisons unifamiliales',
          'Condominiums',
          'Duplex / Triplex',
          'Rénovations majeures',
          'Développements résidentiels',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_projets_an',
        label: 'Nombre de projets par année',
        type: 'select',
        options: ['1-5 projets', '6-15 projets', '16-30 projets', '30+ projets'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Soumissions / Estimations',
          'Gestion de chantiers',
          'Coordination sous-traitants',
          'Suivi budgets / Coûts',
          'Gestion des permis',
          'Inspection / Contrôle qualité',
          'Facturation clients',
          'Gestion des échéances',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'Logiciel estimation (PlanSwift, etc.)',
          'Logiciel gestion projets construction',
          'Excel',
          'AutoCAD (lecture plans)',
          'Autre',
        ],
        required: false,
      },
      {
        id: 'gestion_chantiers',
        label: 'Comment gérez-vous vos chantiers ?',
        type: 'select',
        options: [
          'Papier / Notes',
          'Excel / Tableurs',
          'Application mobile',
          'Logiciel dédié',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires',
        type: 'multiselect',
        options: [
          'Code du bâtiment',
          'Règlements municipaux',
          'CNESST (sécurité)',
          'Garantie habitation (RBQ)',
          'Environnement',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Plans projets',
          'Devis / Estimations',
          'Contrats clients',
          'Photos chantier',
          'Factures sous-traitants',
          'Historique projets',
        ],
        required: true,
      },
      {
        id: 'travail_distance',
        label: 'Travail à distance / Hybride',
        type: 'select',
        options: [
          '100% en présentiel',
          'Hybride (bureau + chantier)',
          'Principalement à distance',
          'Flexible',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Accès plans à distance',
          'Communication chantier-bureau',
          'Partage fichiers',
          'Sauvegardes',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['1 jour', '3 jours', '1 semaine'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Suivi budgets / Dépassements',
          'Coordination sous-traitants',
          'Gestion échéances projets',
          'Communication chantier-bureau',
          'Manque de visibilité projets',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== CLINIQUES MÉDECINE ==========
  {
    metierId: 'cliniques_medecine',
    titre: 'Besoins métier – Cliniques de médecine',
    description: 'Comprendre les réalités des cliniques médicales : dossiers patients, rendez-vous, facturation, conformité.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de services médicaux',
        type: 'multiselect',
        options: [
          'Médecine familiale',
          'Médecine générale',
          'Services spécialisés',
          'Urgences mineures',
          'Vaccination',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_medecins',
        label: 'Nombre de médecins',
        type: 'select',
        options: ['1-3 médecins', '4-10 médecins', '11-25 médecins', '25+ médecins'],
        required: true,
      },
      {
        id: 'nombre_patients_jour',
        label: 'Nombre de patients par jour (approximatif)',
        type: 'select',
        options: ['Moins de 50', '50-100', '100-200', '200+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Prise de rendez-vous',
          'Dossiers patients électroniques (DPE)',
          'Facturation RAMQ / Assurances',
          'Ordonnances',
          'Résultats d\'examens',
          'Communication avec patients',
          'Gestion de la paie',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'DPE (Dossier patient électronique)',
          'Système de rendez-vous',
          'Facturation RAMQ',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'gestion_rdv',
        label: 'Comment gérez-vous les rendez-vous ?',
        type: 'select',
        options: [
          'Carnet papier',
          'Système de rendez-vous simple',
          'Intégré au DPE',
          'Application en ligne',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires',
        type: 'multiselect',
        options: [
          'Protection renseignements santé',
          'Loi 25',
          'Collège des médecins',
          'RAMQ',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Dossiers patients',
          'Historique médical',
          'Ordonnances',
          'Résultats examens',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['15 minutes', '1 heure', '4 heures'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Accès dossiers patients',
          'Sécurité données santé',
          'Intégration systèmes',
          'Gestion rendez-vous',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== CLINIQUES SPÉCIALISÉES ==========
  {
    metierId: 'cliniques_specialisees',
    titre: 'Besoins métier – Cliniques spécialisées',
    description:
      'Comprendre les réalités des cliniques spécialisées : équipements spécifiques, flux patients, examens, rapports, conformité.',
    fields: [
      {
        id: 'types_specialites',
        label: 'Types de spécialités',
        type: 'multiselect',
        options: [
          'Imagerie médicale (radiologie, IRM, CT scan)',
          'Cardiologie',
          'Orthopédie',
          'Dermatologie',
          'Clinique de douleur',
          'Autre spécialité',
        ],
        required: true,
      },
      {
        id: 'volume_patients',
        label: 'Volume de patients par jour (approximatif)',
        type: 'select',
        options: ['Moins de 30', '30-80', '80-150', '150+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Prise de rendez-vous',
          'Orientation / admission patients',
          'Examens / actes spécialisés',
          'Production de rapports',
          'Transmission de résultats aux médecins référents',
          'Facturation RAMQ / assurances',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'Dossiers patients électroniques (DPE)',
          'Systèmes d’imagerie (PACS / RIS)',
          'Logiciels de planification de traitements',
          'Système de facturation RAMQ',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Protection des renseignements de santé',
          'Loi 25',
          'Collèges professionnels (médecins, techniciens, etc.)',
          'RAMQ / Facturation',
          'Normes pour équipements médicaux',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Dossiers patients',
          'Résultats d’examens / images',
          'Rapports médicaux',
          'Historique des visites / traitements',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité des systèmes d’imagerie (PACS / RIS)',
          'Intégration DPE ↔ systèmes spécialisés',
          'Connexion sécurisée pour partage de résultats',
          'Disponibilité des systèmes de rendez-vous',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['15 minutes', '1 heure', '4 heures'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Gestion de la capacité (rendez-vous vs équipements disponibles)',
          'Temps de réponse des systèmes d’imagerie',
          'Intégration entre multiples systèmes (DPE, PACS, facturation)',
          'Sécurité et confidentialité des données sensibles',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== DENTISTES / CLINIQUES DENTAIRES ==========
  {
    metierId: 'dentistes',
    titre: 'Besoins métier – Dentistes / Cliniques dentaires',
    description:
      'Comprendre les réalités des cliniques dentaires : rendez-vous, dossiers, imagerie, équipements, facturation et conformité.',
    fields: [
      {
        id: 'nombre_chaises',
        label: 'Nombre de salles / chaises dentaires',
        type: 'select',
        options: ['1-2', '3-5', '6-10', '10+'],
        required: true,
      },
      {
        id: 'volume_patients',
        label: 'Volume de patients par jour',
        type: 'select',
        options: ['Moins de 20', '20-50', '50-100', '100+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Prise de rendez-vous',
          'Accueil / admission',
          'Actes dentaires (consultations, traitements, chirurgies)',
          'Imagerie (radiographies, scans)',
          'Facturation RAMQ / assurances privées',
          'Rappels de rendez-vous / suivi',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'Logiciel de gestion de clinique dentaire',
          'Dossier patient (notes cliniques, historique)',
          'Logiciel d’imagerie dentaire',
          'Système de rendez-vous / rappels automatisés',
          'Facturation RAMQ / assurances',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Ordre des dentistes',
          'Normes de contrôle infection / stérilisation',
          'Protection des renseignements personnels',
          'RAMQ / Facturation',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Dossiers patients (historique traitements)',
          'Imageries dentaires',
          'Plan de traitement',
          'Calendrier de rendez-vous',
          'Informations d’assurance / couverture',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité du logiciel clinique',
          'Intégration DME ↔ imagerie ↔ facturation',
          'Systèmes de rappels de rendez-vous (SMS, courriel)',
          'Sauvegardes régulières des dossiers et images',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['15 minutes', '1 heure', '4 heures'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Temps perdu à naviguer entre plusieurs systèmes',
          'Annulations / no-show des patients',
          'Gestion des rappels / hygiène préventive',
          'Sécurité / confidentialité des images et dossiers',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== OPTOMÉTRISTES ==========
  {
    metierId: 'optometristes',
    titre: 'Besoins métier – Optométristes',
    description:
      'Comprendre les réalités des cliniques d’optométrie : examens, dossiers, imagerie oculaire, vente de lunettes / lentilles.',
    fields: [
      {
        id: 'types_services',
        label: 'Types de services offerts',
        type: 'multiselect',
        options: [
          'Examens de la vue',
          'Suivi de pathologies (glaucome, DMLA, etc.)',
          'Vente de lunettes',
          'Vente de lentilles cornéennes',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'volume_patients',
        label: 'Volume de patients par jour',
        type: 'select',
        options: ['Moins de 20', '20-40', '40-80', '80+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Prise de rendez-vous',
          'Dossiers patients / historique',
          'Examens visuels / tests',
          'Imagerie oculaire (rétine, cornée, etc.)',
          'Choix montures et verres',
          'Commande auprès de laboratoires',
          'Facturation et réclamations assurances',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'Logiciel de gestion de clinique d’optométrie',
          'Dossier patient électronique',
          'Logiciel d’imagerie oculaire',
          'Systèmes de commande aux laboratoires',
          'Système de point de vente (lunetterie)',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Ordre des optométristes',
          'Protection des renseignements de santé',
          'Loi 25',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Dossiers patients (ordonnances, historisation)',
          'Images oculaires',
          'Historique des achats (lunettes, lentilles)',
          'Commandes en cours auprès des laboratoires',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité du logiciel clinique',
          'Intégration clinique ↔ lunetterie ↔ laboratoires',
          'Systèmes de rappel de rendez-vous et de remplacement lentilles',
          'Sauvegardes régulières des dossiers et images',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['15 minutes', '1 heure', '4 heures'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Gestion des rappels (examens périodiques)',
          'Suivi des commandes laboratoires',
          'Expérience client (parcours clinique ↔ boutique)',
          'Sécurité / confidentialité des données de santé',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== RESTAURANTS ==========
  {
    metierId: 'restaurants',
    titre: 'Besoins métier – Restaurants',
    description: 'Comprendre les réalités des restaurants : POS, réservations, inventaire, facturation.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Type de restaurant',
        type: 'select',
        options: [
          'Restaurant à service complet',
          'Restaurant rapide / Fast-food',
          'Café / Bistro',
          'Traiteur',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_places',
        label: 'Nombre de places / Capacité',
        type: 'select',
        options: ['Moins de 50 places', '50-100 places', '100-200 places', '200+ places'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Prise de commandes (POS)',
          'Gestion inventaire',
          'Réservations',
          'Facturation clients',
          'Gestion de la paie',
          'Commandes livraison',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes utilisés',
        type: 'multiselect',
        options: [
          'Système POS',
          'Gestion inventaire',
          'Réservations',
          'Livraison (Uber Eats, DoorDash, etc.)',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'integration_livraison',
        label: 'Intégration avec plateformes de livraison',
        type: 'select',
        options: [
          'Oui, intégrées au POS',
          'Oui, mais séparées',
          'Non, pas de livraison',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires',
        type: 'multiselect',
        options: [
          'Hygiène alimentaire',
          'Sécurité',
          'Taxes',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Transactions POS',
          'Inventaire',
          'Réservations',
          'Factures',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['15 minutes', '30 minutes', '2 heures'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Disponibilité POS',
          'Gestion inventaire',
          'Intégration livraison',
          'Synchronisation multi-points',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== CONCESSIONNAIRES AUTOMOBILES ==========
  {
    metierId: 'concessionnaires',
    titre: 'Besoins métier – Concessionnaires automobiles',
    description: 'Comprendre les réalités des concessionnaires : DMS, inventaire véhicules, service, financement.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Services offerts',
        type: 'multiselect',
        options: [
          'Vente véhicules neufs',
          'Vente véhicules usagés',
          'Service / Réparation',
          'Pièces',
          'Financement',
        ],
        required: true,
      },
      {
        id: 'marques',
        label: 'Marques représentées',
        type: 'multiselect',
        options: [
          'Ford',
          'GM (Chevrolet, GMC)',
          'Chrysler / Dodge / Jeep',
          'Toyota',
          'Honda',
          'Nissan',
          'Hyundai / Kia',
          'Volkswagen',
          'BMW',
          'Mercedes-Benz',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Gestion inventaire véhicules',
          'Vente véhicules',
          'Service réparation',
          'Gestion pièces',
          'Financement',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes utilisés',
        type: 'multiselect',
        options: [
          'DMS (Dealer Management System)',
          'Système OEM',
          'Gestion inventaire',
        ],
        required: true,
      },
      {
        id: 'integration_oem',
        label: 'Intégration avec systèmes OEM',
        type: 'select',
        options: [
          'Oui, intégration complète',
          'Partielle',
          'Non',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires',
        type: 'multiselect',
        options: [
          'Protection consommateurs',
          'Garanties',
          'Taxes',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Inventaire véhicules',
          'Dossiers clients',
          'Historique réparations',
          'Commandes pièces',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['2 heures', '1 jour', '3 jours'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Disponibilité DMS',
          'Intégration OEM',
          'Gestion inventaire',
          'Service client',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== PIÈCES AUTOMOBILES / ACCESSOIRES ==========
  {
    metierId: 'pieces_automobile',
    titre: 'Besoins métier – Pièces automobiles / Accessoires',
    description:
      'Comprendre les réalités des entreprises de pièces auto : inventaire très détaillé, compatibilités, commandes, service aux garages et au détail.',
    fields: [
      {
        id: 'types_clients',
        label: 'Types de clients',
        type: 'multiselect',
        options: [
          'Garages indépendants',
          'Concessionnaires',
          'Particuliers (comptoir)',
          'Flottes / entreprises',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Recherche de pièces et compatibilité véhicule',
          'Gestion d’inventaire (magasin et entrepôts)',
          'Commandes fournisseurs',
          'Livraison rapide vers garages / clients',
          'Retours et garanties',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'Catalogue électronique de pièces',
          'ERP / gestion d’inventaire',
          'POS comptoir',
          'Intégrations avec systèmes garages / concessionnaires',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Compatibilités pièces ↔ véhicules',
          'Stocks temps réel multi-sites',
          'Historique de commandes clients',
          'Historique de retours / garanties',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité du catalogue électronique',
          'Synchronisation inventaire multi-sites / entrepôts',
          'Intégration commandes garages / partenaires',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['1 heure', '4 heures', '1 jour'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Erreurs de pièces (mauvaise compatibilité)',
          'Ruptures de stock sur pièces critiques',
          'Temps de recherche information trop long',
          'Intégration limitée avec clients garages / concessionnaires',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== INGÉNIEURS (ÉLECTRIQUE / AUTOMATISATION) ==========
  {
    metierId: 'ingenieurs_electrique',
    titre: 'Besoins métier – Ingénieurs (Électrique / Automatisation)',
    description: 'Comprendre les réalités des bureaux d\'ingénierie électrique : conception, schémas, automatisation, conformité.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de projets / services',
        type: 'multiselect',
        options: [
          'Conception électrique (bâtiments)',
          'Automatisation industrielle',
          'Systèmes de contrôle',
          'Éclairage',
          'Sécurité / Alarme',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_ingenieurs',
        label: 'Nombre d\'ingénieurs / employés',
        type: 'select',
        options: ['1-5 personnes', '6-15 personnes', '16-50 personnes', '50+ personnes'],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Logiciels utilisés',
        type: 'multiselect',
        options: [
          'AutoCAD Electrical',
          'Revit MEP',
          'ETAP / SKM (calculs)',
          'PLC / HMI (programmation)',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Conception schémas électriques',
          'Calculs de charge',
          'Programmation PLC',
          'Gestion de projets',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires',
        type: 'multiselect',
        options: [
          'Ordre des ingénieurs du Québec (OIQ)',
          'Code électrique',
          'CNESST',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Schémas électriques',
          'Plans',
          'Programmes PLC',
          'Spécifications',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['2 heures', '1 jour', '3 jours'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Gestion versions schémas',
          'Intégration CAO',
          'Partage fichiers',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== CONSULTANTS ==========
  {
    metierId: 'consultants',
    titre: 'Besoins métier – Consultants',
    description: 'Comprendre les réalités des cabinets de conseil : gestion de projets, facturation, livrables, collaboration.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de services de conseil',
        type: 'multiselect',
        options: [
          'Conseil en gestion',
          'Conseil stratégique',
          'Conseil TI',
          'Conseil RH',
          'Conseil marketing',
          'Conseil financier',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_consultants',
        label: 'Nombre de consultants / employés',
        type: 'select',
        options: ['1-5 personnes', '6-15 personnes', '16-50 personnes', '50+ personnes'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Gestion de projets clients',
          'Facturation / Time tracking',
          'Livrables / Rapports',
          'Communication clients',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Logiciels utilisés',
        type: 'multiselect',
        options: [
          'Gestion projets',
          'Time tracking',
          'Facturation',
          'CRM',
          'Autre',
        ],
        required: false,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['2 heures', '1 jour', '3 jours'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Gestion projets',
          'Facturation temps',
          'Livrables',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== CONSEILLERS FINANCIERS ==========
  {
    metierId: 'conseillers_financiers',
    titre: 'Besoins métier – Conseillers financiers / Planificateurs',
    description: 'Comprendre les réalités des conseillers financiers : portefeuilles clients, conformité, facturation.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de services financiers',
        type: 'multiselect',
        options: [
          'Planification financière',
          'Gestion de portefeuilles',
          'Assurance',
          'Retraite',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_clients',
        label: 'Nombre de clients',
        type: 'select',
        options: ['Moins de 50', '50-150', '150-500', '500+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Gestion portefeuilles clients',
          'Planification financière',
          'Facturation',
          'Communication clients',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires',
        type: 'multiselect',
        options: [
          'AMF',
          'Protection données',
          'Conformité',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Portefeuilles clients',
          'Plans financiers',
          'Historique transactions',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['15 minutes', '1 heure', '4 heures'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Sécurité données',
          'Conformité',
          'Gestion portefeuilles',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== CONSTRUCTION COMMERCIALE ==========
  {
    metierId: 'construction_commerciale',
    titre: 'Besoins métier – Construction commerciale / industrielle',
    description: 'Comprendre les réalités des constructeurs commerciaux : projets complexes, coordination, conformité.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de projets',
        type: 'multiselect',
        options: [
          'Bureaux',
          'Commerces',
          'Industriel',
          'Institutionnel',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Soumissions',
          'Gestion chantiers',
          'Coordination',
          'Suivi budgets',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires',
        type: 'multiselect',
        options: [
          'Code du bâtiment',
          'CNESST',
          'RBQ',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['1 jour', '3 jours', '1 semaine'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Coordination',
          'Gestion budgets',
          'Échéances',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== GESTIONNAIRE DE PROJETS DE CONSTRUCTION ==========
  {
    metierId: 'gestionnaire_projets_construction',
    titre: 'Besoins métier – Gestionnaire de projets de construction',
    description:
      'Comprendre les réalités de la gestion de projets de construction : coordination multi-intervenants, contrôle des coûts, échéanciers, documentation et conformité.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de projets gérés',
        type: 'multiselect',
        options: [
          'Résidentiel (multi logements)',
          'Commercial',
          'Industriel',
          'Institutionnel',
          'Rénovations majeures',
          'Infrastructures / ouvrages civils',
          'Autre',
        ],
        required: true,
        helpText: 'Sélectionnez les types de projets que vous gérez le plus souvent',
      },
      {
        id: 'volume_projets',
        label: 'Volume moyen de projets actifs en parallèle',
        type: 'select',
        options: ['1-3', '4-8', '9-15', '15+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Planification (WBS, jalons, échéancier)',
          'Gestion des budgets / coûts',
          'Appels d’offres / achats',
          'Gestion des sous-traitants',
          'Gestion des changements (change orders)',
          'Suivi de chantier (journaux, photos, inspections)',
          'Gestion des documents (plans, devis, RFI, submittals)',
          'Rapports d’avancement (clients / direction)',
          'Gestion SST (sécurité chantier)',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels utilisés',
        type: 'multiselect',
        options: [
          'Procore / Autodesk Construction Cloud',
          'MS Project / Planner',
          'Primavera P6',
          'BIM / Revit (lecture/coordination)',
          'SharePoint / Google Drive (documents)',
          'ERP / comptabilité (Sage, QuickBooks, etc.)',
          'Outil de suivi temps / feuille de temps',
          'Courriel / calendrier',
          'Autre',
        ],
        required: false,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'CNESST (SST)',
          'RBQ',
          'Code du bâtiment',
          'Règlements municipaux / permis',
          'Contrats / exigences clients (audit, traçabilité)',
          'Conservation des documents',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Plans / devis / addendas',
          'Échéanciers',
          'Budgets / coûts / estimations',
          'RFI / submittals',
          'Journaux de chantier / photos / inspections',
          'Contrats / change orders',
          'Correspondances (courriels, comptes rendus)',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Accès mobile sur chantier (tablette/téléphone)',
          'Synchronisation en temps réel (documents, RFI, photos)',
          'Collaboration multi-entreprises (partage et permissions)',
          'Production de rapports (avancement, coûts, risques)',
          'Intégration documents ↔ budget ↔ facturation',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['2 heures', '1 jour', '3 jours'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Contrôle documentaire (versioning, accès)',
          'Coordination multi-intervenants',
          'Visibilité coûts vs budget (en temps réel)',
          'Suivi des changements et impacts',
          'Mobilité chantier (capture photos / inspections)',
          'Sécurité des accès et traçabilité',
          'Standardisation des processus (RFI, submittals)',
          'Intégrations (PM ↔ ERP ↔ documents)',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== GESTION IMMOBILIÈRE ==========
  {
    metierId: 'gestion_immobiliere',
    titre: 'Besoins métier – Gestion immobilière / Syndicats',
    description: 'Comprendre les réalités de la gestion immobilière : immeubles, locataires, entretien, finances.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de propriétés gérées',
        type: 'multiselect',
        options: [
          'Résidentiel',
          'Commercial',
          'Mixte',
          'Condominiums',
        ],
        required: true,
      },
      {
        id: 'nombre_immeubles',
        label: 'Nombre d\'immeubles gérés',
        type: 'select',
        options: ['1-5', '6-20', '21-50', '50+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Gestion locataires',
          'Collecte loyers',
          'Entretien',
          'Finances',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Logiciels utilisés',
        type: 'multiselect',
        options: [
          'Gestion immobilière',
          'Comptabilité',
          'Autre',
        ],
        required: false,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['1 jour', '3 jours', '1 semaine'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Gestion locataires',
          'Collecte loyers',
          'Entretien',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== ÉVALUATION DE PROJETS IMMOBILIER ==========
  {
    metierId: 'evaluation_projets_immobilier',
    titre: 'Besoins métier – Firme d\'évaluation de projets Immobilier',
    description:
      'Comprendre les réalités des firmes d’évaluation : collecte de comparables, analyses, rédaction de rapports, confidentialité et délais serrés.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de mandats',
        type: 'multiselect',
        options: [
          'Évaluation résidentielle',
          'Évaluation commerciale',
          'Évaluation industrielle',
          'Études de faisabilité / meilleure utilisation',
          'Analyse de rentabilité / pro forma',
          'Due diligence / audit',
          'Évaluation pour financement / institutions',
          'Évaluation pour expropriation / litige',
          'Autre',
        ],
        required: true,
        helpText: 'Sélectionnez les types de mandats réalisés le plus souvent',
      },
      {
        id: 'volume_mandats',
        label: 'Volume moyen de mandats / rapports par mois',
        type: 'select',
        options: ['1-10', '11-30', '31-60', '60+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Collecte de données & comparables',
          'Visites / inspections terrain',
          'Analyse financière / modélisation',
          'Rédaction de rapports',
          'Révision / contrôle qualité',
          'Transmission au client (portail, courriel sécurisé)',
          'Gestion des demandes urgentes',
          'Facturation & suivi',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels utilisés',
        type: 'multiselect',
        options: [
          'Excel / Google Sheets',
          'Argus / outils d’analyse immobilière',
          'SIG / cartographie (ArcGIS, QGIS, etc.)',
          'CRM (HubSpot, Salesforce, etc.)',
          'Gestion documentaire (SharePoint, Drive, etc.)',
          'Outil de rédaction / gabarits de rapports',
          'Signature électronique',
          'Bases de données comparables (Centris/MLS, etc.)',
          'Autre',
        ],
        required: false,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'OEAQ / normes de pratique (si applicable)',
          'Confidentialité client',
          'Conservation des dossiers (périodes légales)',
          'Loi 25 / renseignements personnels',
          'Exigences des prêteurs / institutions',
          'Traçabilité des pièces / chaîne de possession',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Rapports d’évaluation',
          'Données comparables & historiques',
          'Photos / plans / relevés',
          'Modèles financiers (pro forma)',
          'Données clients & contrats',
          'Documents légaux (titres, baux, etc.)',
          'Courriels & communications',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['1 heure', '4 heures', '1 jour'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Standardisation et qualité des rapports',
          'Accès rapide aux données comparables',
          'Gestion documentaire & recherche',
          'Collaboration interne (révision)',
          'Sécurité / confidentialité',
          'Mobilité terrain (tablette, photos)',
          'Intégrations (CRM ↔ documents ↔ facturation)',
          'Délais serrés / urgences',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== HÔPITAUX ==========
  {
    metierId: 'hopitaux',
    titre: 'Besoins métier – Hôpitaux / Centres hospitaliers',
    description: 'Comprendre les réalités des hôpitaux : dossiers patients, systèmes critiques, conformité, 24/7.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de services hospitaliers',
        type: 'multiselect',
        options: [
          'Urgences',
          'Chirurgie',
          'Soins intensifs',
          'Imagerie médicale',
          'Laboratoire',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_lits',
        label: 'Nombre de lits',
        type: 'select',
        options: ['Moins de 50', '50-200', '200-500', '500+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Dossiers patients',
          'Ordonnances',
          'Résultats examens',
          'Facturation',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes utilisés',
        type: 'multiselect',
        options: [
          'DPE',
          'Imagerie (PACS)',
          'Laboratoire',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires',
        type: 'multiselect',
        options: [
          'Protection renseignements santé',
          'Loi 25',
          'Collège des médecins',
          'RAMQ',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Dossiers patients',
          'Imagerie médicale',
          'Résultats examens',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['15 minutes', '1 heure', '4 heures'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Disponibilité 24/7',
          'Sécurité données',
          'Intégration systèmes',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== PHARMACIES ==========
  {
    metierId: 'pharmacies',
    titre: 'Besoins métier – Pharmacies',
    description: 'Comprendre les réalités des pharmacies : prescriptions, inventaire, facturation RAMQ, conformité.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Services offerts',
        type: 'multiselect',
        options: [
          'Dispensation médicaments',
          'Conseils pharmaceutiques',
          'Vaccination',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Dispensation',
          'Gestion inventaire',
          'Facturation RAMQ',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes utilisés',
        type: 'multiselect',
        options: [
          'Système pharmacie',
          'Facturation RAMQ',
          'Gestion inventaire',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires',
        type: 'multiselect',
        options: [
          'Ordre des pharmaciens',
          'RAMQ',
          'Protection données',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['15 minutes', '1 heure', '4 heures'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Disponibilité système',
          'Gestion inventaire',
          'Facturation RAMQ',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== MANUFACTURIER ALIMENTAIRE ==========
  {
    metierId: 'manufacturier_alimentaire',
    titre: 'Besoins métier – Manufacturier alimentaire',
    description: 'Comprendre les réalités de la fabrication alimentaire : production, traçabilité, conformité, qualité.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de produits',
        type: 'multiselect',
        options: [
          'Produits transformés',
          'Produits frais',
          'Produits congelés',
          'Boissons',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Production',
          'Traçabilité',
          'Contrôle qualité',
          'Gestion stocks',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes utilisés',
        type: 'multiselect',
        options: [
          'ERP manufacturier',
          'Traçabilité',
          'Contrôle qualité',
          'Autre',
        ],
        required: false,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires',
        type: 'multiselect',
        options: [
          'MAPAQ',
          'ACIA',
          'Traçabilité',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Recettes',
          'Traçabilité',
          'Contrôle qualité',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['1 heure', '4 heures', '1 jour'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Traçabilité',
          'Contrôle qualité',
          'Gestion production',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== TRANSFORMATION & DISTRIBUTION (PÊCHE) ==========
  {
    metierId: 'transformation_distribution_peche',
    titre: 'Besoins métier – Transformation & distribution (pêche)',
    description:
      'Comprendre les réalités de la transformation et de la distribution des produits de la pêche : chaîne du froid, traçabilité, conformité (MAPAQ, ACIA), qualité, logistique et distribution.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types d\'activités',
        type: 'multiselect',
        options: [
          'Transformation (filetage, congélation, mise en conserve)',
          'Distribution / Vente au détail',
          'Vente en gros / Grossistes',
          'Exportation',
          'Marée (vente poisson frais)',
          'Aquaculture (élevage)',
          'Autre',
        ],
        required: true,
        helpText: 'Sélectionnez les activités principales de l\'entreprise',
      },
      {
        id: 'types_produits',
        label: 'Types de produits',
        type: 'multiselect',
        options: [
          'Poisson frais',
          'Poisson congelé',
          'Fruits de mer',
          'Produits transformés (surimi, panés, etc.)',
          'Conserves',
          'Produits fumés',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Réception / Contrôle qualité entrant',
          'Transformation (coupe, congélation, conditionnement)',
          'Traçabilité (lot, origine, température)',
          'Gestion de la chaîne du froid',
          'Gestion des stocks (frais, congelé)',
          'Planification de la production',
          'Expédition / Livraison',
          'Facturation clients / fournisseurs',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'ERP (production / distribution)',
          'Système de traçabilité',
          'Gestion de la chaîne du froid (température)',
          'Gestion des stocks / entrepôt',
          'Facturation / Comptabilité',
          'Excel / Outils maison',
          'Autre',
        ],
        required: false,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'MAPAQ (aliments)',
          'ACIA (fédéral, export)',
          'Traçabilité (origine, lot)',
          'Chaîne du froid (température, enregistrements)',
          'Salubrité (HACCP, BPF)',
          'Étiquetage / Allergènes',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Traçabilité (lot, fournisseur, date)',
          'Températures (réception, entreposage, transport)',
          'Contrôles qualité',
          'Inventaire (frais, congelé, DLC)',
          'Commandes clients / fournisseurs',
          'Coûts et marges par produit',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité système de traçabilité',
          'Enregistrement continu des températures (chaîne du froid)',
          'Accès aux données en entrepôt / quai',
          'Facturation et inventaire à jour',
          'Communication avec fournisseurs / clients',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['1 heure', '4 heures', '1 jour'],
        required: true,
        helpText: 'Impact sur la traçabilité et la chaîne du froid',
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Traçabilité et conformité réglementaire',
          'Maîtrise de la chaîne du froid',
          'Gestion des DLC et inventaire',
          'Productivité en transformation',
          'Intégration systèmes (production, stocks, facturation)',
          'Qualité et contrôle entrant / sortant',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        placeholder: 'Ex: sites multiples, export, certifications particulières...',
        required: false,
      },
    ],
  },

  // ========== MANUFACTURIER MÉCANIQUE / MÉTALLURGIE ==========
  {
    metierId: 'manufacturier_mecanique',
    titre: 'Besoins métier – Manufacturier mécanique / Métallurgie',
    description:
      'Comprendre les réalités des manufacturiers mécaniques : conception, usinage, production, qualité, maintenance, intégration atelier–TI.',
    fields: [
      {
        id: 'types_produits',
        label: 'Types de produits fabriqués',
        type: 'multiselect',
        options: [
          'Pièces usinées sur mesure',
          'Production en série',
          'Équipements / machines complètes',
          'Composants mécano-soudés',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'volume_production',
        label: 'Volume de production',
        type: 'select',
        options: [
          'Petites séries / prototypage',
          'Séries moyennes',
          'Grandes séries / production de masse',
          'Mixte (projets + séries)',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Ingénierie / conception (CAO)',
          'Programmation CNC',
          'Planification de la production (ordonnancement)',
          'Contrôle qualité (mesures, inspection)',
          'Gestion des stocks de matières premières',
          'Maintenance préventive / corrective',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'ERP manufacturier',
          'CAO (SolidWorks, Inventor, etc.)',
          'CAM / FAO (programmation CNC)',
          'MES (Manufacturing Execution System)',
          'Logiciel de métrologie / inspection',
          'Excel / Outils maison',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / normatives',
        type: 'multiselect',
        options: [
          'Normes de qualité (ex: ISO 9001)',
          'Normes de sécurité machines (CNESST, CSA)',
          'Exigences clients (plans, tolérances, traçabilité)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Dossiers de fabrication / gammes',
          'Programmes CNC',
          'Plans CAO',
          'Résultats d’inspection',
          'Historique de production par lot / commande',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Connexion atelier–serveurs (accès aux plans et programmes CNC)',
          'Disponibilité ERP / MES pour la production',
          'Sauvegarde et gestion des versions CAO/CAM',
          'Intégration qualité ↔ production ↔ ingénierie',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['1 heure', '4 heures', '1 jour'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Planification et ordonnancement complexes',
          'Perte de temps à chercher les bons plans / versions',
          'Difficulté à suivre la performance (OEE, temps de cycle)',
          'Intégration limitée entre ERP, CAO/CAM et atelier',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== MANUFACTURIER PLASTIQUE / COMPOSITE ==========
  {
    metierId: 'manufacturier_plastique',
    titre: 'Besoins métier – Manufacturier plastique / Composite',
    description:
      'Comprendre les réalités des manufacturiers plastiques / composites : moules, injection, extrusion, qualité, traçabilité.',
    fields: [
      {
        id: 'types_procedes',
        label: 'Types de procédés',
        type: 'multiselect',
        options: [
          'Injection plastique',
          'Extrusion',
          'Thermoformage',
          'Composite (fibres, résines)',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'gestion_moules',
        label: 'Gestion des moules / outillages',
        type: 'select',
        options: [
          'Suivi manuel (Excel / papier)',
          'Système maison / base de données interne',
          'Système structuré (ERP / MES avec gestion des moules)',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Préparation et changement de moules',
          'Planification des lots de production',
          'Contrôle qualité (dimensions, apparence, propriétés)',
          'Traçabilité des lots (matières et produits finis)',
          'Gestion des recettes / paramètres de machines',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'ERP manufacturier',
          'MES / suivi temps réel des machines',
          'SCADA / supervision',
          'Logiciels de laboratoire qualité',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / normatives',
        type: 'multiselect',
        options: [
          'Normes qualité (ex: ISO, IATF, selon industrie)',
          'Exigences clients (traçabilité, certificats)',
          'Environnement / gestion des déchets',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Historique des paramètres machines par lot',
          'Résultats de tests qualité',
          'Traçabilité matières premières',
          'Historique des non-conformités',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Connexion machines ↔ systèmes (MES/SCADA)',
          'Intégration qualité ↔ production',
          'Disponibilité ERP pour la planification',
          'Sauvegarde des recettes / programmes machines',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['1 heure', '4 heures', '1 jour'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Changements de moules fréquents / temps de setup',
          'Non-conformités récurrentes / rebut',
          'Difficulté à relier paramètres machines et qualité',
          'Traçabilité complexe des lots',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== MANUFACTURIER TEXTILE / VÊTEMENTS ==========
  {
    metierId: 'manufacturier_textile',
    titre: 'Besoins métier – Manufacturier textile / Vêtements',
    description:
      'Comprendre les réalités des manufacturiers textiles : collections, tailles, couleurs, saisons, sous-traitance internationale.',
    fields: [
      {
        id: 'types_produits',
        label: 'Types de produits textiles',
        type: 'multiselect',
        options: [
          'Vêtements mode',
          'Uniformes / vêtements de travail',
          'Textiles techniques',
          'Linge de maison',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'structure_collections',
        label: 'Structure des collections',
        type: 'select',
        options: [
          'Collections saisonnières (ex: printemps/été, automne/hiver)',
          'Collections continues / basiques',
          'Mixte (basiques + saisons)',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Conception / design',
          'Prototypage / échantillons',
          'Lancement de collections',
          'Planification de la production (interne ou sous-traitée)',
          'Gestion des tailles / couleurs / variantes',
          'Gestion des retours / invendus',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'ERP textile / mode',
          'PLM (Product Lifecycle Management)',
          'Outils de conception (CAO textile, design)',
          'Outils d’ordonnancement / planification',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Étiquetage (composition, pays d’origine, entretien)',
          'Normes de sécurité / inflammabilité',
          'Environnement / responsabilité sociale (fournisseurs)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'BOM / nomenclatures produits',
          'Tailles / couleurs / variantes',
          'Commandes clients et prévisions',
          'Performance par collection / modèle',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Synchronisation données produits entre ERP, PLM et clients',
          'Visibilité stocks multi-sites / multi-entrepôts',
          'Suivi de production chez les sous-traitants',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['4 heures', '1 jour', '3 jours'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Gestion complexe des tailles / couleurs / variantes',
          'Prévisions difficiles (tendances, saisons)',
          'Suivi de production chez les sous-traitants',
          'Gestion des invendus / retours',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== MANUFACTURIER BOIS / MEUBLES ==========
  {
    metierId: 'manufacturier_bois',
    titre: 'Besoins métier – Manufacturier bois / Meubles',
    description:
      'Comprendre les réalités des manufacturiers bois : design, découpe, assemblage, finition, projets sur mesure vs série.',
    fields: [
      {
        id: 'types_produits',
        label: 'Types de produits en bois',
        type: 'multiselect',
        options: [
          'Meubles résidentiels',
          'Meubles commerciaux',
          'Armoires de cuisine / salle de bain',
          'Composants architecturaux (escaliers, moulures, etc.)',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'mode_production',
        label: 'Mode de production principal',
        type: 'select',
        options: [
          'Sur mesure / projets',
          'Série / catalogue',
          'Mixte (sur mesure + catalogue)',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Conception / dessin (CAO, 3D)',
          'Découpe CNC',
          'Assemblage',
          'Finition (teinture, vernis, peinture)',
          'Installation sur site (si applicable)',
          'Gestion des commandes clients / projets',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'CAO bois (ex: Cabinet Vision, SolidWorks, etc.)',
          'ERP manufacturier',
          'Logiciels de découpe / nesting CNC',
          'Outils de gestion de projets',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Normes de santé et sécurité (poussières, machines)',
          'Normes environnementales (colles, vernis, émissions COV)',
          'Exigences architecturales ou de design',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Plans et dessins de fabrication',
          'Listes de coupe et optimisations',
          'Commandes clients / plans d’assemblage',
          'Historique des projets',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Connexion CAO ↔ CNC (fichiers de coupe)',
          'Disponibilité ERP pour planifier la production',
          'Intégration entre devis, dessins et production',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['4 heures', '1 jour', '3 jours'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Gestion de projets sur mesure complexes',
          'Optimisation de la matière première',
          'Lien entre devis, dessins et production',
          'Visibilité sur la rentabilité par projet',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== TRANSPORT ROUTIER ==========
  {
    metierId: 'transport_routier',
    titre: 'Besoins métier – Transport routier / Camionnage',
    description: 'Comprendre les réalités du transport : gestion flotte, routes, traçabilité, conformité.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de transport',
        type: 'multiselect',
        options: [
          'Transport général',
          'Transport réfrigéré',
          'Transport dangereux',
          'Messagerie',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_vehicules',
        label: 'Nombre de véhicules',
        type: 'select',
        options: ['1-5', '6-20', '21-50', '50+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Planification routes',
          'Suivi véhicules',
          'Facturation',
          'Gestion flotte',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes utilisés',
        type: 'multiselect',
        options: [
          'TMS',
          'GPS',
          'Gestion flotte',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires',
        type: 'multiselect',
        options: [
          'Réglementation transport',
          'Heures conduite',
          'Sécurité',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Positions véhicules',
          'Livraisons',
          'Factures',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['2 heures', '1 jour', '3 jours'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Optimisation routes',
          'Suivi véhicules',
          'Gestion flotte',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== TRANSPORT EXPRESS / MESSAGERIE ==========
  {
    metierId: 'transport_express',
    titre: 'Besoins métier – Messagerie / Transport express',
    description:
      'Comprendre les réalités du transport express : livraisons rapides, suivi en temps réel, optimisation des routes, promesse de service serrée.',
    fields: [
      {
        id: 'types_services',
        label: 'Types de services offerts',
        type: 'multiselect',
        options: [
          'Livraison locale (même jour)',
          'Livraison régionale (24-48 heures)',
          'Livraison nationale / internationale',
          'Services urgents / critiques (ex: médical, pièces critiques)',
        ],
        required: true,
      },
      {
        id: 'taille_flotte',
        label: 'Taille de la flotte (véhicules)',
        type: 'select',
        options: ['1-10', '11-50', '51-200', '200+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Prise de commandes (web, téléphone, intégrations clients)',
          'Planification des routes / tournées',
          'Ramassage / dépôt colis',
          'Suivi en temps réel (tracking)',
          'Preuve de livraison (POD)',
          'Facturation / tarification',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'TMS (Transport Management System)',
          'Système de dispatch / planification des routes',
          'App mobiles chauffeurs (scan, POD, GPS)',
          'Portail clients (suivi en ligne)',
          'Intégrations EDI / API avec clients',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Règlementation transport (heures de conduite, sécurité)',
          'Douanes / documentation (si international)',
          'Protection des données (tracking, information clients)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Commandes / colis à livrer',
          'Positions véhicules en temps réel',
          'Preuves de livraison (signature, photos)',
          'Historique de performance (retards, incidents)',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité du TMS et du dispatch',
          'Connexion mobile (app chauffeurs) fiable',
          'Intégration GPS / tracking',
          'Portails clients disponibles (suivi colis, rapports)',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['15 minutes', '1 heure', '4 heures'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Respect des délais de livraison promis',
          'Suivi en temps réel pour clients exigeants',
          'Optimisation routes / coûts carburant',
          'Gestion des exceptions (retards, colis manquants)',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== ENTREPOSAGE / ENTREPÔTS ==========
  {
    metierId: 'entreposage',
    titre: 'Besoins métier – Entreposage / Entrepôts',
    description:
      'Comprendre les réalités de l’entreposage : réception, stockage, préparation de commandes, inventaire et sécurité.',
    fields: [
      {
        id: 'type_activite',
        label: 'Type d’activité principale',
        type: 'multiselect',
        options: [
          'Entreposage dédié pour un client',
          'Entreposage multi-clients (3PL)',
          'Centre de distribution interne',
          'Transbordement / cross-docking',
        ],
        required: true,
      },
      {
        id: 'surface_entrepot',
        label: 'Surface d’entreposage',
        type: 'select',
        options: ['< 10 000 pi²', '10 000 – 50 000 pi²', '50 000 – 200 000 pi²', '200 000+ pi²'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Réception des marchandises',
          'Mise en stock / emplacement',
          'Préparation de commandes (picking, packing)',
          'Expédition',
          'Gestion des retours',
          'Inventaires cycliques / annuels',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'WMS (Warehouse Management System)',
          'ERP avec module entrepôt',
          'App mobiles scanneurs (code-barres, RFID)',
          'TMS pour intégration transport',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Santé et sécurité au travail (CNESST)',
          'Normes de stockage (produits dangereux, température contrôlée)',
          'Traçabilité (santé, pharma, alimentaire si applicable)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Inventaire temps réel par emplacement',
          'Historique des mouvements (traçabilité)',
          'Performance opérationnelle (préparation commandes, erreurs)',
          'Historique des réceptions / expéditions',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité du WMS',
          'Connexion scanneurs / terminaux mobiles',
          'Intégration WMS ↔ ERP ↔ TMS',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['1 heure', '4 heures', '1 jour'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Exactitude d’inventaire',
          'Productivité en préparation de commandes',
          'Traçabilité des produits',
          'Intégration avec transport et clients',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== LOGISTIQUE / SUPPLY CHAIN ==========
  {
    metierId: 'logistique',
    titre: 'Besoins métier – Logistique / Supply chain',
    description:
      'Comprendre les réalités de la logistique : planification globale, flux entre sites, visibilité bout-en-bout, collaboration avec partenaires.',
    fields: [
      {
        id: 'role_logistique',
        label: 'Rôle de la fonction logistique',
        type: 'multiselect',
        options: [
          'Planification de la demande et des approvisionnements',
          'Gestion des entrepôts / centres de distribution',
          'Gestion transport (entrant / sortant)',
          'Coordination avec partenaires 3PL',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Planification de la demande',
          'Planification de la distribution',
          'Planification transport (routes, chargements)',
          'Suivi des niveaux de stocks multi-sites',
          'Gestion des exceptions (ruptures, retards)',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'ERP (modules logistique / supply)',
          'APS (Advanced Planning & Scheduling)',
          'TMS',
          'WMS',
          'Portails fournisseurs / clients',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Prévisions de demande',
          'Niveaux de stocks multi-sites',
          'Ordres d’achat / de transfert',
          'Historique de performance (OTIF, délais, coûts)',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Données fiables et à jour sur stocks et flux',
          'Intégration entre ERP, WMS, TMS et APS',
          'Visibilité temps réel ou quasi temps réel (tracking, exceptions)',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['4 heures', '1 jour', '3 jours'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Ruptures de stock / surplus',
          'Manque de visibilité bout-en-bout',
          'Manque d’intégration systèmes',
          'Difficulté à planifier avec précision',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== E-COMMERCE ==========
  {
    metierId: 'ecommerce',
    titre: 'Besoins métier – E-commerce / Vente en ligne',
    description: 'Comprendre les réalités du e-commerce : plateforme, inventaire, paiements, expédition.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Type de e-commerce',
        type: 'select',
        options: [
          'B2C (Business to Consumer)',
          'B2B (Business to Business)',
          'Marketplace',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Gestion commandes',
          'Inventaire',
          'Paiements',
          'Expédition',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Plateformes / Systèmes utilisés',
        type: 'multiselect',
        options: [
          'Shopify',
          'WooCommerce',
          'Magento',
          'Plateforme sur mesure',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['15 minutes', '1 heure', '4 heures'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Disponibilité site',
          'Gestion inventaire',
          'Paiements',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== DÉVELOPPEMENT LOGICIEL ==========
  {
    metierId: 'developpement_logiciel',
    titre: 'Besoins métier – Développement logiciel',
    description: 'Comprendre les réalités du développement : code, CI/CD, collaboration, déploiement.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de développement',
        type: 'multiselect',
        options: [
          'Applications web',
          'Applications mobiles',
          'Logiciels desktop',
          'APIs / Services',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_developpeurs',
        label: 'Nombre de développeurs',
        type: 'select',
        options: ['1-5', '6-15', '16-50', '50+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Développement',
          'CI/CD',
          'Tests',
          'Déploiement',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Outils utilisés',
        type: 'multiselect',
        options: [
          'Git / GitHub / GitLab',
          'CI/CD (Jenkins, etc.)',
          'Cloud (AWS, Azure, GCP)',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['15 minutes', '1 heure', '4 heures'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Disponibilité service',
          'CI/CD',
          'Collaboration',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== SAAS / SERVICES CLOUD ==========
  {
    metierId: 'saas',
    titre: 'Besoins métier – SaaS / Services cloud',
    description:
      'Comprendre les réalités des entreprises SaaS : plateforme multi-tenant, disponibilité, sécurité, facturation récurrente, roadmap produit.',
    fields: [
      {
        id: 'types_saas',
        label: 'Type de solution SaaS',
        type: 'multiselect',
        options: [
          'B2B (entreprises)',
          'B2C (grand public)',
          'Vertical (spécifique à un secteur)',
          'Plateforme / API',
        ],
        required: true,
      },
      {
        id: 'environnements',
        label: 'Environnements utilisés',
        type: 'multiselect',
        options: [
          'Cloud public (AWS, Azure, GCP)',
          'Cloud privé',
          'Hybride',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Développement / CI-CD',
          'Exploitation / SRE (Site Reliability Engineering)',
          'Support et succès client',
          'Gestion des abonnements / facturation récurrente',
          'Gestion de la roadmap produit',
        ],
        required: true,
      },
      {
        id: 'outils_principaux',
        label: 'Plateformes / outils principaux',
        type: 'multiselect',
        options: [
          'Git / plateforme CI-CD',
          'Surveillance / observabilité (logs, métriques, traces)',
          'Plateforme de facturation SaaS',
          'CRM / outil de support',
          'Gestion des incidents (ITSM, PagerDuty, etc.)',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Protection des données (Loi 25, RGPD, etc.)',
          'Normes de sécurité (ISO 27001, SOC2, etc.)',
          'Exigences spécifiques clients (secteurs régulés)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Données clients et de configuration',
          'Logs et métriques de la plateforme',
          'Données de facturation / abonnements',
          'Code source et pipelines CI-CD',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité élevée de la plateforme (SLA)',
          'Automatisation CI-CD fiable',
          'Observabilité et gestion des incidents',
          'Sécurité et gestion des accès (IAM, secrets, etc.)',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable de la plateforme',
        type: 'select',
        options: ['< 5 minutes', '5-30 minutes', '30-120 minutes'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Fiabilité / disponibilité (SLA, incidents)',
          'Scalabilité (montée en charge)',
          'Sécurité et conformité',
          'Vitesse d’évolution du produit (time-to-market)',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== CYBERSÉCURITÉ / SERVICES SÉCURITÉ ==========
  {
    metierId: 'cybersecurite',
    titre: 'Besoins métier – Cybersécurité / Services sécurité',
    description:
      'Comprendre les réalités des firmes de cybersécurité : SOC, services gérés, projets, conformité, gestion des incidents.',
    fields: [
      {
        id: 'type_services',
        label: 'Types de services offerts',
        type: 'multiselect',
        options: [
          'Services gérés (SOC, MDR, etc.)',
          'Projets (implémentations sécurité)',
          'Gouvernance / risque / conformité (GRC)',
          'Tests d’intrusion / Red Team',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Surveillance sécurité 24/7',
          'Gestion des alertes et incidents',
          'Gestion des vulnérabilités',
          'Gestion des changements / déploiements sécurité',
          'Rapports aux clients',
        ],
        required: true,
      },
      {
        id: 'outils_principaux',
        label: 'Outils et plateformes utilisés',
        type: 'multiselect',
        options: [
          'SIEM',
          'EDR / XDR',
          'Scanners de vulnérabilités',
          'Plateforme de tickets / ITSM',
          'Outils de tests d’intrusion',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Normes de sécurité (ISO 27001, SOC2, NIST, etc.)',
          'Exigences spécifiques clients (finance, santé, secteur public)',
          'Confidentialité des données client',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Logs et télémétrie sécurité',
          'Dossiers et rapports d’incidents',
          'Inventaire des actifs clients',
          'Runbooks / procédures de réponse',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité 24/7 des plateformes SOC',
          'Intégrations multiples (clients, outils sécurité)',
          'Accès sécurisé et segmenté aux environnements clients',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des plateformes SOC',
        type: 'select',
        options: ['< 5 minutes', '5-30 minutes', '30-120 minutes'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Volume d’alertes et bruit (fatigue des analystes)',
          'Recrutement / rétention des talents',
          'Standardisation des services entre clients',
          'Intégration d’outils multiples et hétérogènes',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== TÉLÉCOMMUNICATIONS ==========
  {
    metierId: 'telecommunications',
    titre: 'Besoins métier – Télécommunications',
    description:
      'Comprendre les réalités des fournisseurs télécom : réseaux, services voix/données, support, facturation, conformité.',
    fields: [
      {
        id: 'types_services',
        label: 'Types de services offerts',
        type: 'multiselect',
        options: [
          'Internet résidentiel',
          'Internet affaires',
          'Téléphonie (fixe, IP)',
          'Mobilité',
          'Services gérés réseau / sécurité',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Provisioning services (activation, changements)',
          'Surveillance réseau et incidents',
          'Support client (N1/N2/N3)',
          'Facturation et gestion des comptes',
          'Gestion capacité / planification réseau',
        ],
        required: true,
      },
      {
        id: 'outils_principaux',
        label: 'Systèmes / outils principaux',
        type: 'multiselect',
        options: [
          'OSS / BSS (support opérations et facturation)',
          'Systèmes de surveillance réseau (NMS)',
          'CRM / plateforme de support',
          'Portails clients / partenaires',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Régulateurs télécom',
          'Exigences d’interception légale',
          'Protection des renseignements clients',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Inventaire des services / circuits',
          'Données de performance réseau',
          'Dossiers incidents / tickets',
          'Données de facturation / consommation',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité des systèmes OSS/BSS',
          'Surveillance réseau 24/7',
          'Intégration entre systèmes réseau, facturation et CRM',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['< 5 minutes', '5-30 minutes', '30-120 minutes'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Gestion des incidents majeurs',
          'Expérience client (délai de résolution, communication)',
          'Modernisation des systèmes legacy',
          'Planification capacité réseau face à la demande croissante',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== SYNDICATS ==========
  {
    metierId: 'syndicats',
    titre: 'Besoins métier – Syndicats / Associations',
    description: 'Comprendre les réalités des syndicats : membres, communication, conventions collectives, conformité.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de services',
        type: 'multiselect',
        options: [
          'Représentation membres',
          'Négociations',
          'Gestion griefs',
          'Formation',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_membres',
        label: 'Nombre de membres',
        type: 'select',
        options: ['Moins de 500', '500-2000', '2000-5000', '5000+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Communication membres',
          'Gestion adhésions',
          'Négociations',
          'Gestion griefs',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires',
        type: 'multiselect',
        options: [
          'Loi 25',
          'Conventions collectives',
          'Protection données',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['2 heures', '1 jour', '3 jours'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Communication membres',
          'Gestion adhésions',
          'Négociations',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== ÉCOLES PRIVÉES ==========
  {
    metierId: 'ecoles_privees',
    titre: 'Besoins métier – Écoles privées',
    description:
      'Comprendre les réalités des écoles privées : gestion des élèves, pédagogie, communication parents, conformité et sécurité.',
    fields: [
      {
        id: 'niveaux_scolaires',
        label: 'Niveaux scolaires couverts',
        type: 'multiselect',
        options: [
          'Préscolaire',
          'Primaire',
          'Secondaire',
          'Collégial',
        ],
        required: true,
      },
      {
        id: 'nombre_eleves',
        label: 'Nombre d’élèves',
        type: 'select',
        options: ['Moins de 300', '300-800', '800-1500', '1500+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Inscription / réinscription',
          'Gestion des dossiers élèves',
          'Gestion des horaires / groupes',
          'Suivi académique (notes, bulletins)',
          'Communication école-parents-élèves',
          'Gestion de la discipline et incidents',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'Système de gestion scolaire (SIS)',
          'Plateforme d’apprentissage (LMS)',
          'Portail parents / élèves',
          'Outils de communication (courriels, SMS, notifications)',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Ministère de l’Éducation (MEQ)',
          'Protection des renseignements personnels (Loi 25)',
          'Sécurité des élèves (protocoles, accès)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Dossiers élèves (académique et administratif)',
          'Résultats scolaires / bulletins',
          'Données de présence / absences',
          'Informations parents / contacts d’urgence',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité du SIS et du portail parents/élèves',
          'Connectivité réseau / Wi-Fi pour classe numérique',
          'Sécurité des accès (contrôle d’accès, mots de passe, etc.)',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['1 heure', '4 heures', '1 jour'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Communication école-parents-élèves',
          'Suivi académique et interventions rapides',
          'Protection des données sensibles',
          'Adoption des outils numériques par les enseignants',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== ORGANISMES DE FORMATION ==========
  {
    metierId: 'organismes_formation',
    titre: 'Besoins métier – Organismes de formation',
    description:
      'Comprendre les réalités des organismes de formation : catalogues de cours, inscription, formation présentielle et en ligne, financement.',
    fields: [
      {
        id: 'types_formation',
        label: 'Types de formation offerts',
        type: 'multiselect',
        options: [
          'Formation en présentiel',
          'Formation en ligne (synchrone)',
          'Formation en ligne (asynchrone, e-learning)',
          'Programmes longs (certifications) / courts (ateliers)',
        ],
        required: true,
      },
      {
        id: 'volume_apprenants',
        label: 'Nombre d’apprenants par année',
        type: 'select',
        options: ['Moins de 200', '200-1000', '1000-5000', '5000+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Gestion du catalogue de formations',
          'Inscriptions et paiements',
          'Diffusion des contenus (présentiel et en ligne)',
          'Suivi de la progression / complétion',
          'Évaluation / certification',
          'Rapports de présence / financement (ex: Emploi-Québec)',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'LMS (Learning Management System)',
          'Plateforme d’inscription / paiement',
          'Outils de visioconférence',
          'Outils de création de contenus e-learning',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Dossiers apprenants',
          'Résultats / complétionen',
          'Historique de participation',
          'Données de financement / subventions',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité du LMS',
          'Qualité de la visioconférence',
          'Accès sécurisé aux contenus en ligne',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['1 heure', '4 heures', '1 jour'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Expérience apprenant (accès simple et fluide)',
          'Suivi de progression',
          'Qualité des données pour rapports / financement',
          'Adoption des outils numériques par formateurs',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== AGENCE EN ÉDUCATION MÉDICALE ==========
  {
    metierId: 'agence_education_medicale',
    titre: 'Besoins métier – Agence en éducation médicale',
    description:
      'Comprendre les réalités des agences d’éducation médicale : conception de contenus, accréditation, diffusion multi-canal, suivi des apprenants et rapports.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de programmes / services',
        type: 'multiselect',
        options: [
          'Développement de contenus (modules, e-learning)',
          'Gestion d’événements (congrès, ateliers)',
          'Webinaires / formations synchrones',
          'Programmes asynchrones (LMS)',
          'Accréditation / conformité (CME)',
          'Évaluation / examens / attestations',
          'Communication / marketing (inscriptions, infolettres)',
          'Support aux organisations médicales',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'volume_participants',
        label: 'Nombre de participants / apprenants par année',
        type: 'select',
        options: ['Moins de 500', '500-2000', '2000-10000', '10000+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Gestion du catalogue / programmes',
          'Gestion des inscriptions / accès',
          'Diffusion (webinaire, LMS, présentiel)',
          'Gestion des contenus et versions',
          'Suivi de la progression / complétion',
          'Évaluations / quiz / examens',
          'Production d’attestations / crédits',
          'Rapports d’accréditation / audits',
          'Support participants (technique et administratif)',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels utilisés',
        type: 'multiselect',
        options: [
          'LMS (Moodle, Canvas, TalentLMS, etc.)',
          'Plateforme webinaires (Zoom, Teams, Webex, etc.)',
          'Outils de création e-learning (Articulate, Captivate, etc.)',
          'CRM / marketing automation',
          'Gestion d’événements / billetterie',
          'Gestion documentaire (SharePoint, Drive, etc.)',
          'Signature électronique',
          'Outils d’analytique / BI',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Accréditation CME / exigences des ordres (selon juridiction)',
          'Conflits d’intérêts / transparence',
          'Loi 25 / renseignements personnels',
          'Protection des données (accès, journalisation)',
          'Droits d’auteur / licences de contenu',
          'PCI-DSS (paiements, si applicable)',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Contenus pédagogiques (sources, médias, gabarits)',
          'Données apprenants (profil, progression)',
          'Résultats d’évaluation / attestations',
          'Rapports d’accréditation / audits',
          'Données clients / partenaires',
          'Transactions / inscriptions / facturation (si applicable)',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité du LMS et des webinaires',
          'Accès sécurisé (SSO, MFA, rôles)',
          'Performance streaming (vidéo) et bande passante',
          'Intégrations (CRM ↔ inscriptions ↔ LMS)',
          'Analytique (suivi complétion, rapports)',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['30 minutes', '4 heures', '1 jour'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Rapports et conformité d’accréditation',
          'Gestion des contenus et versions (qualité)',
          'Expérience utilisateur des apprenants',
          'Sécurité / confidentialité des données',
          'Automatisation des attestations / crédits',
          'Support lors des événements en direct',
          'Intégrations et doublons de données',
          'Mesure de l’impact / analytics',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== OBNL / ORGANISMES COMMUNAUTAIRES ==========
  {
    metierId: 'obnl',
    titre: 'Besoins métier – OBNL / Organismes communautaires',
    description:
      'Comprendre les réalités des OBNL : mission, membres, financement, reddition de comptes, gouvernance.',
    fields: [
      {
        id: 'types_obnl',
        label: 'Type d’OBNL',
        type: 'multiselect',
        options: [
          'Communautaire / services aux citoyens',
          'Culturel / sportif',
          'Fondation / philanthropie',
          'Association professionnelle / sectorielle',
        ],
        required: true,
      },
      {
        id: 'taille_organisation',
        label: 'Taille de l’organisation',
        type: 'select',
        options: ['0-5 employés', '6-20 employés', '21-50 employés', '50+ employés'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Gestion des membres / bénéficiaires',
          'Gestion des programmes / services',
          'Collecte de dons / financement',
          'Rapports aux bailleurs de fonds / reddition de comptes',
          'Communication avec membres / communauté',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'CRM / gestion des membres',
          'Plateforme de dons en ligne',
          'Outils de gestion de projets / programmes',
          'Outils de communication (infolettre, réseaux sociaux)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Liste des membres / bénéficiaires',
          'Données de participation aux programmes',
          'Historique des dons / contributions',
          'Rapports et indicateurs pour bailleurs de fonds',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Gestion des membres en ligne',
          'Campagnes de financement efficaces (web, infolettre, réseaux sociaux)',
          'Production de rapports pour subventions / bailleurs',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['1 jour', '3 jours', '1 semaine'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Ressources TI limitées / dépendance à des bénévoles',
          'Données éparpillées (Excel, papier, outils gratuits)',
          'Difficulté à produire des rapports pour bailleurs de fonds',
          'Visibilité limitée sur l’impact et les résultats',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== HÔTELS ==========
  {
    metierId: 'hotels',
    titre: 'Besoins métier – Hôtels / Hébergement',
    description: 'Comprendre les réalités des hôtels : réservations, gestion chambres, facturation, service client.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Type d\'établissement',
        type: 'select',
        options: [
          'Hôtel',
          'Motel',
          'Auberge',
          'Résidence',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_chambres',
        label: 'Nombre de chambres',
        type: 'select',
        options: ['Moins de 20', '20-50', '50-100', '100+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Réservations',
          'Gestion chambres',
          'Facturation',
          'Service client',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes utilisés',
        type: 'multiselect',
        options: [
          'PMS (Property Management System)',
          'Réservations',
          'Facturation',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['30 minutes', '2 heures', '1 jour'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Disponibilité PMS',
          'Réservations',
          'Service client',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== GARAGES ==========
  {
    metierId: 'garages',
    titre: 'Besoins métier – Garages / Réparation automobile',
    description: 'Comprendre les réalités des garages : service réparation, inventaire pièces, facturation.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de services',
        type: 'multiselect',
        options: [
          'Réparation générale',
          'Mécanique',
          'Carrosserie',
          'Entretien',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Gestion rendez-vous',
          'Réparations',
          'Inventaire pièces',
          'Facturation',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes utilisés',
        type: 'multiselect',
        options: [
          'Gestion garage',
          'Inventaire',
          'Facturation',
          'Autre',
        ],
        required: false,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['2 heures', '1 jour', '3 jours'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Gestion rendez-vous',
          'Inventaire pièces',
          'Facturation',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== CATERING / TRAITEURS ==========
  {
    metierId: 'catering',
    titre: 'Besoins métier – Traiteurs / Catering',
    description:
      'Comprendre les réalités des traiteurs : événements, menus, production, logistique, gestion des coûts et de la qualité.',
    fields: [
      {
        id: 'types_evenements',
        label: 'Types d’événements servis',
        type: 'multiselect',
        options: [
          'Mariages',
          'Événements corporatifs',
          'Festivals / grands événements',
          'Écoles / institutions',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'volume_evenements',
        label: 'Volume moyen d’événements par mois',
        type: 'select',
        options: ['1-5', '6-15', '16-30', '30+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Prise de commandes / devis',
          'Planification des menus',
          'Achat / gestion des matières premières',
          'Production en cuisine',
          'Logistique de livraison / service sur site',
          'Facturation / encaissement',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'Logiciel de gestion de traiteur / événements',
          'Outils de planification (calendrier, planning équipes)',
          'ERP / comptabilité',
          'Excel / Outils maison',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Hygiène et salubrité alimentaire (MAPAQ)',
          'Traçabilité des aliments',
          'Allergènes et restrictions alimentaires',
          'Santé et sécurité du travail',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Listes d’ingrédients / recettes',
          'Commandes clients / devis',
          'Planning événements et ressources',
          'Historique de rentabilité des événements',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Planning en temps réel (événements, équipes, logistique)',
          'Suivi des coûts matières',
          'Communication avec clients (changements de dernière minute)',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['4 heures', '1 jour', '3 jours'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Gestion des pics d’activité (fin de semaine, saisons)',
          'Contrôle des coûts et marges par événement',
          'Coordination logistique (livraisons, personnel)',
          'Gestion des allergènes et restrictions alimentaires',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== CENTRES FUNÉRAIRES ==========
  {
    metierId: 'centre_funeraire',
    titre: 'Besoins métier – Centre funéraire / Accompagnement des familles endeuillées',
    description:
      'Comprendre les réalités des centres funéraires : dossiers sensibles, coordination, production de documents, confidentialité et disponibilité.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de services offerts',
        type: 'multiselect',
        options: [
          'Arrangements funéraires',
          'Cérémonies / célébrations',
          'Crémation',
          'Inhumation',
          'Préarrangements / prévoyance',
          'Gestion columbarium / concessions',
          'Transport / logistique',
          'Autre',
        ],
        required: true,
        helpText: 'Sélectionnez les services principaux offerts',
      },
      {
        id: 'volume_services',
        label: 'Volume moyen de dossiers / services par mois',
        type: 'select',
        options: ['1-10', '11-30', '31-60', '60+'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Accueil & prise en charge',
          'Collecte d’informations et documents',
          'Planification des cérémonies',
          'Coordination avec fournisseurs (floriste, célébrant, etc.)',
          'Gestion des rendez-vous',
          'Gestion des contrats & consentements',
          'Facturation & paiements',
          'Suivi post-service / accompagnement',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels utilisés',
        type: 'multiselect',
        options: [
          'Logiciel de gestion funéraire (dossiers, registres)',
          'Calendrier / planification',
          'Gestion documentaire',
          'Comptabilité',
          'CRM / suivi familles',
          'Signature électronique',
          'Site web / avis de décès',
          'Autre',
        ],
        required: false,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Loi 25 / renseignements personnels',
          'Conservation des dossiers & registres',
          'Consentements (crémation, disposition, etc.)',
          'Normes/obligations provinciales (si applicable)',
          'PCI-DSS (paiements)',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Informations du défunt',
          'Informations des familles / proches',
          'Contrats & autorisations',
          'Documents légaux (certificats, formulaires)',
          'Coordonnées fournisseurs',
          'Factures / paiements',
          'Historique des communications',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Accès rapide aux dossiers (contexte urgent)',
          'Disponibilité de l’horaire (salles, équipes, fournisseurs)',
          'Production de documents (contrats, formulaires, consentements)',
          'Communication interne (équipe / intervenants externes)',
          'Portail web / avis de décès',
          'Paiements et facturation',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['30 minutes', '2 heures', '1 jour'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Confidentialité & accès contrôlé',
          'Standardisation des documents & consentements',
          'Coordination et planification multi-intervenants',
          'Saisie d’informations rapide (mobile)',
          'Archivage & recherche',
          'Intégration facturation / paiements',
          'Expérience famille (formulaires et signatures en ligne)',
          'Disponibilité des systèmes',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== AMÉNAGEMENT PAYSAGER / ENTRETIEN DE PELOUSES ==========
  {
    metierId: 'amenagement_paysager',
    titre: 'Besoins métier – Aménagement paysager / Entretien de pelouses',
    description: 'Comprendre les réalités des entreprises d\'aménagement paysager : saisonnalité, gestion de flotte, planification de routes, facturation.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Types de services offerts',
        type: 'multiselect',
        options: [
          'Traitement de pelouses',
          'Fertilisation',
          'Aération du sol',
          'Contrôle des mauvaises herbes',
          'Déchaumage',
          'Sursemis',
          'Tonte de pelouse',
          'Aménagement paysager',
          'Plantation',
          'Irrigation / Arrosage',
          'Déneigement (hiver)',
          'Autre',
        ],
        required: true,
        helpText: 'Sélectionnez tous les services offerts par votre entreprise',
      },
      {
        id: 'nombre_employes',
        label: 'Nombre d\'employés',
        type: 'select',
        options: ['1-5 personnes', '6-15 personnes', '16-30 personnes', '30+ personnes'],
        required: true,
      },
      {
        id: 'type_clients',
        label: 'Types de clients',
        type: 'multiselect',
        options: [
          'Résidentiel (particuliers)',
          'Commercial (entreprises)',
          'Institutionnel (écoles, hôpitaux)',
          'Municipalités',
          'Condominiums',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_clients',
        label: 'Nombre approximatif de clients actifs',
        type: 'select',
        options: ['Moins de 50 clients', '50-150 clients', '150-500 clients', '500+ clients'],
        required: true,
      },
      {
        id: 'saisonnalite',
        label: 'Saisonnalité des opérations',
        type: 'multiselect',
        options: [
          'Printemps (mars-mai) - Période de pointe',
          'Été (juin-août) - Période de pointe',
          'Automne (septembre-novembre) - Période modérée',
          'Hiver (décembre-février) - Période creuse / Déneigement',
          'Opérations à l\'année',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Planification des visites / Routes',
          'Gestion des contrats clients',
          'Suivi des traitements / Applications',
          'Facturation par visite / contrat',
          'Gestion de la flotte de véhicules',
          'Gestion de l\'inventaire (produits, équipements)',
          'Coordination des équipes terrain',
          'Communication avec clients',
          'Gestion des rendez-vous / Visites',
          'Suivi météo / Conditions',
        ],
        required: true,
      },
      {
        id: 'gestion_visites',
        label: 'Comment gérez-vous actuellement les visites clients ?',
        type: 'select',
        options: [
          'Carnet papier / Notes',
          'Excel / Tableurs',
          'Application mobile simple',
          'Logiciel de gestion dédié',
          'Système maison / Sur mesure',
        ],
        required: true,
      },
      {
        id: 'facturation',
        label: 'Modèle de facturation',
        type: 'select',
        options: [
          'Facturation par visite',
          'Facturation par contrat saisonnier',
          'Facturation par contrat annuel',
          'Mixte (visites + contrats)',
        ],
        required: true,
      },
      {
        id: 'flotte_vehicules',
        label: 'Gestion de la flotte de véhicules / équipements',
        type: 'select',
        options: [
          'Aucun système formel',
          'Suivi manuel (carnets, notes)',
          'Application mobile simple',
          'Logiciel de gestion de flotte',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Logiciels / systèmes utilisés actuellement',
        type: 'multiselect',
        options: [
          'Aucun logiciel spécialisé',
          'Logiciel de gestion paysager',
          'Application mobile terrain',
          'GPS / Suivi véhicules',
          'Gestion de flotte',
          'Facturation / Comptabilité',
          'Microsoft 365 / Google Workspace',
          'Autre',
        ],
        required: false,
      },
      {
        id: 'inventaire',
        label: 'Gestion de l\'inventaire (produits, équipements)',
        type: 'select',
        options: [
          'Aucun suivi formel',
          'Suivi manuel (carnets, Excel)',
          'Logiciel d\'inventaire',
          'Intégré au système de gestion',
        ],
        required: true,
      },
      {
        id: 'travail_terrain',
        label: 'Travail terrain / Mobile',
        type: 'select',
        options: [
          '100% terrain (équipes mobiles)',
          'Mixte (bureau + terrain)',
          'Principalement bureau',
        ],
        required: true,
      },
      {
        id: 'communication_clients',
        label: 'Communication avec clients',
        type: 'multiselect',
        options: [
          'Téléphone uniquement',
          'Courriel',
          'SMS / Texto',
          'Portail client en ligne',
          'Application mobile client',
          'Réseaux sociaux',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Permis d\'application de pesticides (MAPAQ)',
          'Règlements municipaux (épandage)',
          'Normes environnementales',
          'Sécurité au travail (CNESST)',
          'Assurances responsabilité',
          'Conformité produits (Santé Canada)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques pour l\'entreprise',
        type: 'multiselect',
        options: [
          'Historique des traitements par client',
          'Plans de traitement / Calendriers',
          'Contrats clients',
          'Factures / Relevés',
          'Positions véhicules / Équipes',
          'Inventaire produits / Équipements',
          'Photos avant/après traitements',
          'Météo / Conditions terrain',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Accès aux données clients en terrain',
          'GPS / Suivi véhicules en temps réel',
          'Application mobile terrain fiable',
          'Communication équipes terrain-bureau',
          'Synchronisation données terrain-bureau',
          'Accès internet mobile (4G/5G)',
          'Sauvegardes automatiques',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d\'arrêt maximal acceptable',
        type: 'select',
        options: ['2 heures', '1 jour', '3 jours'],
        required: true,
        helpText: 'Au-delà de ce délai, les opérations terrain sont fortement impactées',
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Planification routes inefficace / Perte de temps',
          'Communication difficile équipes terrain-bureau',
          'Facturation incomplète / Retards',
          'Manque de visibilité sur les opérations terrain',
          'Gestion inventaire imprécise',
          'Suivi traitements / Historique client incomplet',
          'Coordination équipes difficile',
          'Perte de contrats / Clients insatisfaits',
          'Gestion flotte inefficace',
          'Conformité réglementaire / Risques',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        placeholder: 'Ex: Expansion prévue, nouveaux services, défis spécifiques saisonniers...',
        required: false,
      },
    ],
  },

  // ========== COMMERCE DE DÉTAIL (GÉNÉRAL) ==========
  {
    metierId: 'commerce_detail',
    titre: 'Besoins métier – Commerce de détail (Général)',
    description:
      'Comprendre les réalités du commerce de détail : expérience client en magasin, inventaire, caisses, omnicanal, saisonnalité.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Type de commerce de détail',
        type: 'multiselect',
        options: [
          'Mode / Vêtements',
          'Articles de sport',
          'Électronique',
          'Quincaillerie',
          'Maison / Décoration',
          'Grande surface / Multisecteur',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'nombre_succursales',
        label: 'Nombre de succursales / points de vente',
        type: 'select',
        options: ['1 point de vente', '2-5 points de vente', '6-20 points de vente', '20+ points de vente'],
        required: true,
      },
      {
        id: 'surface_vente',
        label: 'Surface de vente approximative (succursale principale)',
        type: 'select',
        options: ['< 1 000 pi²', '1 000 – 5 000 pi²', '5 000 – 20 000 pi²', '20 000+ pi²'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Point de vente / Caisse (POS)',
          'Gestion des stocks / Inventaire',
          'Réception marchandises',
          'Étiquetage / Prix',
          'Promotions / Rabais',
          'Commandes fournisseurs',
          'Gestion retours / échanges',
          'Relations clients / Service après-vente',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'Système POS (point de vente)',
          'Logiciel de gestion d’inventaire',
          'ERP / Gestion intégrée',
          'CRM clients',
          'Solution de fidélisation',
          'Excel / Outils maison',
          'Aucun système structuré',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'canaux_vente',
        label: 'Canaux de vente utilisés',
        type: 'multiselect',
        options: [
          'Magasin physique uniquement',
          'Site e-commerce propre',
          'Marketplaces (Amazon, etc.)',
          'Réseaux sociaux',
          'Vente B2B (grossistes, distributeurs)',
        ],
        required: true,
      },
      {
        id: 'saisonnalite',
        label: 'Saisonnalité de l’activité',
        type: 'multiselect',
        options: [
          'Relativement stable toute l’année',
          'Pics saisonniers (ex: Noël, rentrée, etc.)',
          'Fortement dépendant des saisons',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Taxe de vente (TPS/TVQ)',
          'Protection des données clients (Loi 25)',
          'Conformité paiements (PCI-DSS)',
          'Normes de santé et sécurité au travail (CNESST)',
          'Affichage des prix / Protection du consommateur',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques pour le commerce',
        type: 'multiselect',
        options: [
          'Inventaire temps réel par succursale',
          'Historique des ventes',
          'Données clients / fidélisation',
          'Marges / Coûts par produit',
          'Promotions et rabais',
          'Performance par canal de vente',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité des caisses (POS) en magasin',
          'Connexion réseau / Internet stable',
          'Synchronisation inventaire (magasin ↔ central ↔ web)',
          'Intégration POS ↔ comptabilité / ERP',
          'Outils de rapports / tableaux de bord',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['15 minutes', '30 minutes', '2 heures', '1 jour'],
        required: true,
        helpText: 'Au-delà de ce délai, les opérations en magasin sont fortement impactées',
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Ruptures d’inventaire fréquentes',
          'Difficulté à avoir une vue globale de l’inventaire',
          'Temps d’attente à la caisse trop long',
          'Manque de données pour décider (ventes, marges, etc.)',
          'Intégration faible entre systèmes (POS, inventaire, comptabilité)',
          'Difficulté à gérer les promotions / prix',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        placeholder:
          'Ex: Projets d’expansion, refonte du concept magasin, lancement e-commerce, enjeux spécifiques actuels...',
        required: false,
      },
    ],
  },

  // ========== COMMERCE ALIMENTAIRE / ÉPICERIES ==========
  {
    metierId: 'commerce_alimentaire',
    titre: 'Besoins métier – Commerce alimentaire / Épiceries',
    description:
      'Comprendre les réalités des commerces alimentaires : produits frais, pertes, traçabilité, sécurité alimentaire, expérience client.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Type de commerce alimentaire',
        type: 'multiselect',
        options: [
          'Épicerie de quartier',
          'Supermarché',
          'Marché spécialisé (bio, santé, produits fins)',
          'Boucherie / Poissonnerie',
          'Boulangerie / Pâtisserie',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'surface_vente',
        label: 'Surface de vente approximative',
        type: 'select',
        options: ['< 2 000 pi²', '2 000 – 10 000 pi²', '10 000 – 40 000 pi²', '40 000+ pi²'],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Réception marchandises / Contrôle qualité',
          'Gestion des stocks (frais, surgelés, sec)',
          'Gestion des dates de péremption',
          'Étiquetage / Prix / Promotions',
          'Point de vente / Caisse (POS)',
          'Gestion des pertes / gaspillage',
          'Commandes fournisseurs / réapprovisionnement',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'POS alimentaire',
          'Logiciel de gestion d’inventaire alimentaire',
          'ERP / Gestion intégrée',
          'Outils de traçabilité (lots, dates, fournisseurs)',
          'Solution d’affichage électronique des prix',
          'Excel / Outils maison',
          'Aucun système structuré',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'traçabilite',
        label: 'Niveau actuel de traçabilité des produits',
        type: 'select',
        options: [
          'Très faible (peu ou pas de suivi des lots / dates)',
          'Basique (dates et lots suivis manuellement)',
          'Structuré (systèmes informatisés, rapports rapides)',
          'Très avancé (traçabilité bout-en-bout, audits fréquents)',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'MAPAQ / Sécurité alimentaire',
          'Normes de chaîne de froid',
          'Traçabilité des produits',
          'Protection des données clients (Loi 25)',
          'Conformité paiements (PCI-DSS)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques pour le commerce alimentaire',
        type: 'multiselect',
        options: [
          'Inventaire temps réel par catégorie (frais, surgelé, sec)',
          'Dates de péremption / lots',
          'Ventes par produit / catégorie',
          'Marges / Pertes alimentaires',
          'Données promotionnelles',
          'Flux de caisses (pics de fréquentation)',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité POS et scans à la caisse',
          'Intégration balances / étiqueteuses ↔ systèmes',
          'Connexion réseau stable en magasin',
          'Synchronisation inventaire et prix (central ↔ succursales)',
          'Outils de rapports pour pertes / gaspillage',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['15 minutes', '30 minutes', '2 heures'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Pertes alimentaires importantes / gaspillage',
          'Gestion complexe des promotions et prix',
          'Difficulté à suivre les dates de péremption',
          'Ruptures de stock fréquentes sur certains produits',
          'Visibilité limitée sur marges et rentabilité par catégorie',
          'Conformité et audits MAPAQ exigeants',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        placeholder:
          'Ex: Projets de rénovation, ajout de sections (prêt-à-manger, boucherie), nouvelles exigences de la bannière, etc.',
        required: false,
      },
    ],
  },

  // ========== COMMERCE SPÉCIALISÉ ==========
  {
    metierId: 'commerce_specialise',
    titre: 'Besoins métier – Commerce spécialisé',
    description:
      'Comprendre les réalités des commerces spécialisés : expertise produit, conseil, gestion d’inventaire niché, expérience client.',
    fields: [
      {
        id: 'resume_activites',
        label: 'Type de commerce spécialisé',
        type: 'multiselect',
        options: [
          'Produits techniques / industriels',
          'Produits de luxe',
          'Produits de niche (ex: plein air, musique, hobby)',
          'Produits professionnels (B2B)',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'type_clients',
        label: 'Types de clients principaux',
        type: 'multiselect',
        options: [
          'Clients grand public (B2C)',
          'Clients professionnels (B2B)',
          'Mixte (B2B + B2C)',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Conseil / vente assistée en magasin',
          'Gestion des stocks par référence technique',
          'Commandes spéciales / sur mesure',
          'Service après-vente / réparation',
          'Gestion des garanties',
          'Relation avec fournisseurs spécialisés',
        ],
        required: true,
      },
      {
        id: 'logiciels_métier',
        label: 'Systèmes / logiciels métier utilisés',
        type: 'multiselect',
        options: [
          'POS / caisse spécialisée',
          'ERP / Gestion intégrée avec fiches techniques',
          'CRM pour suivi clients et projets',
          'Catalogue électronique / PIM',
          'Excel / Outils maison',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'complexite_produits',
        label: 'Complexité des produits vendus',
        type: 'select',
        options: [
          'Produits simples (peu de variantes)',
          'Produits avec plusieurs variantes (taille, couleur, modèle)',
          'Produits très techniques (spécifications détaillées, compatibilités)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques pour le commerce spécialisé',
        type: 'multiselect',
        options: [
          'Inventaire détaillé par référence',
          'Historique d’achats par client',
          'Données de compatibilité / configuration produit',
          'Dossiers de service / réparation',
          'Marges par produit / catégorie',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Accès rapide à l’information produit en magasin',
          'Synchronisation inventaire magasin ↔ entrepôt / web',
          'Intégration POS ↔ ERP ↔ CRM',
          'Outils de recherche produit (compatibilité, filtres techniques)',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['30 minutes', '2 heures', '1 jour'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Difficulté à trouver l’information produit rapidement',
          'Erreur de stock (produit affiché disponible mais manquant)',
          'Mauvaise visibilité sur la rentabilité par produit / segment',
          'Difficulté à suivre les commandes spéciales / projets',
          'Intégration limitée entre systèmes (POS, ERP, CRM)',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        placeholder:
          'Ex: Positionnement haut de gamme, spécialisation technique, croissance prévue, nouveau canal (web, B2B, etc.)',
        required: false,
      },
    ],
  },

  // ========== BANQUES / INSTITUTIONS BANCAIRES ==========
  {
    metierId: 'banques',
    titre: 'Besoins métier – Banques / Institutions bancaires',
    description:
      'Comprendre les réalités des banques : canaux multicanaux, systèmes core banking, paiements, conformité, sécurité et disponibilité 24/7.',
    fields: [
      {
        id: 'segments_cibles',
        label: 'Segments de clientèle principaux',
        type: 'multiselect',
        options: [
          'Particuliers',
          'PME',
          'Grandes entreprises',
          'Institutionnel',
        ],
        required: true,
      },
      {
        id: 'canaux_serveurs',
        label: 'Canaux de service offerts',
        type: 'multiselect',
        options: [
          'Succursales physiques',
          'Centre d’appels',
          'Banque en ligne (web)',
          'Application mobile',
          'Guichets automatiques (GAB)',
          'Conseillers sur la route',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Ouverture de comptes',
          'Transactions courantes (paiements, virements, dépôts)',
          'Octroi de crédit (prêts, marges, cartes)',
          'Gestion des risques et conformité',
          'Détection de fraude',
          'Rapport réglementaire',
        ],
        required: true,
      },
      {
        id: 'systemes_cores',
        label: 'Systèmes coeur (core banking) utilisés',
        type: 'multiselect',
        options: [
          'Core bancaire principal (débits / crédits)',
          'Système cartes de crédit',
          'Plateforme prêts hypothécaires',
          'Systèmes de paiements (Interac, SWIFT, etc.)',
          'Data warehouse / BI',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Régulateurs (BSIF, AMF, etc.)',
          'Lutte contre le blanchiment (LBA/FT)',
          'Sanctions / Embargos',
          'Protection des renseignements personnels (Loi 25, PIPEDA)',
          'Normes PCI-DSS (paiements)',
          'Normes de continuité (BCP/DRP)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Données clients et KYC',
          'Positions de comptes / soldes',
          'Historique de transactions',
          'Données de crédit / risques',
          'Données réglementaires / rapports',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité 24/7 des canaux (web, mobile, GAB)',
          'Latence faible pour les transactions temps réel',
          'Intégration entre core banking et canaux',
          'Capacité de reprise après sinistre (DRP)',
          'Sécurité avancée (IAM, MFA, SOC, SIEM)',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable (systèmes critiques)',
        type: 'select',
        options: ['< 5 minutes', '5-30 minutes', '30-120 minutes'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Disponibilité et performance des canaux clients',
          'Pression réglementaire élevée',
          'Sécurité et cyber-risques',
          'Intégration de systèmes legacy',
          'Lancement rapide de nouveaux produits digitaux',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== ASSURANCES ==========
  {
    metierId: 'assurances',
    titre: 'Besoins métier – Assurances',
    description:
      'Comprendre les réalités des assureurs : souscription, gestion des polices, sinistres, tarification, distribution multi-canal et conformité.',
    fields: [
      {
        id: 'segments_assurance',
        label: 'Segments d’assurance principaux',
        type: 'multiselect',
        options: [
          'Assurance auto',
          'Assurance habitation',
          'Assurance entreprises',
          'Assurance vie',
          'Assurance santé',
          'Autres produits spécialisés',
        ],
        required: true,
      },
      {
        id: 'canaux_distribution',
        label: 'Canaux de distribution',
        type: 'multiselect',
        options: [
          'Agents / Courtiers',
          'Vente directe (centre d’appels)',
          'Vente en ligne (web / mobile)',
          'Partenariats (banques, concessionnaires, etc.)',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Souscription / Émission de polices',
          'Gestion des polices (avenants, renouvellements)',
          'Gestion des sinistres (FNOL, traitement, règlement)',
          'Tarification / Actuariat',
          'Facturation / Encaissement primes',
          'Gestion de la relation client',
        ],
        required: true,
      },
      {
        id: 'systemes_moteurs',
        label: 'Systèmes moteurs utilisés',
        type: 'multiselect',
        options: [
          'Système de gestion des polices',
          'Système de gestion des sinistres',
          'Moteur de tarification / Actuariat',
          'CRM / Portail courtiers',
          'Data warehouse / BI',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'AMF / Régulateurs assurance',
          'Loi sur la distribution de produits et services financiers',
          'Protection des renseignements personnels (Loi 25, PIPEDA)',
          'Lutte contre le blanchiment (LBA/FT)',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Données clients et polices',
          'Historique de sinistres',
          'Tarifs / Modèles actuariels',
          'Données de distribution (performance courtiers / canaux)',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité des systèmes de souscription et sinistres',
          'Intégration front-office ↔ back-office',
          'Portails self-service (clients, courtiers)',
          'Capacités analytiques (BI, actuariat, tarification dynamique)',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable (systèmes critiques)',
        type: 'select',
        options: ['30 minutes', '2 heures', '1 jour'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Modernisation de systèmes legacy',
          'Expérience client et rapidité de traitement des sinistres',
          'Pression réglementaire et reporting',
          'Meilleure utilisation des données pour tarification / prévention',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== COURTAGE / INVESTISSEMENT ==========
  {
    metierId: 'courtage',
    titre: 'Besoins métier – Courtage / Investissement',
    description:
      'Comprendre les réalités du courtage : marchés financiers, plateformes de négociation, conformité, expérience investisseurs.',
    fields: [
      {
        id: 'type_courtage',
        label: 'Type de courtage',
        type: 'multiselect',
        options: [
          'Courtage plein service',
          'Courtage à escompte (en ligne)',
          'Conseillers en placement / Gestion privée',
          'Gestion institutionnelle',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Négociation / exécution d’ordres',
          'Gestion des comptes clients et portefeuilles',
          'Conformité / Surveillance des transactions',
          'Production de relevés et rapports réglementaires',
          'Conseil et recommandation aux clients',
        ],
        required: true,
      },
      {
        id: 'plateformes',
        label: 'Plateformes et systèmes principaux',
        type: 'multiselect',
        options: [
          'Plateforme de négociation (trading)',
          'Système de tenue de positions / P&L',
          'Systèmes de conformité et surveillance (surveillance des transactions, listes de surveillance)',
          'Portails clients (web / mobile)',
          'Outils de recherche et d’analyse marchés',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'Organismes de réglementation (AMF, OCRCVM, etc.)',
          'Règles de connaissance du client (KYC)',
          'Convenance des placements (suitability)',
          'Surveillance des transactions suspectes',
          'Protection des renseignements personnels',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Positions et portefeuilles clients',
          'Historique des transactions',
          'Relevés de comptes',
          'Alertes de conformité / surveillance',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Disponibilité et performance des plateformes de trading',
          'Latence très faible pour l’exécution d’ordres',
          'Flux temps réel de données de marché',
          'Intégration entre systèmes (trading, positions, P&L, conformité)',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable (systèmes de trading)',
        type: 'select',
        options: ['< 5 minutes', '5-30 minutes', '30-120 minutes'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Fiabilité et performance des plateformes de trading',
          'Gestion des risques de marché / opérationnels',
          'Conformité et surveillance accrue',
          'Expérience client (accès, rapidité, information)',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },

  // ========== GESTION DE PATRIMOINE ==========
  {
    metierId: 'gestion_patrimoine',
    titre: 'Besoins métier – Gestion de patrimoine',
    description:
      'Comprendre les réalités de la gestion de patrimoine : conseils personnalisés, portefeuilles complexes, fiscalité, multigénérationnel.',
    fields: [
      {
        id: 'segments_clients',
        label: 'Segments de clientèle servis',
        type: 'multiselect',
        options: [
          'Individus à valeur nette élevée',
          'Familles / Structures multigénérationnelles',
          'Entrepreneurs / Propriétaires d’entreprise',
          'Fondations / Fiducies',
        ],
        required: true,
      },
      {
        id: 'processus_cles',
        label: 'Processus métier critiques',
        type: 'multiselect',
        options: [
          'Planification financière globale',
          'Gestion de portefeuilles',
          'Planification fiscale et successorale',
          'Suivi régulier avec les clients (revues de portefeuille)',
          'Rapport et transparence (relevés, rapports consolidés)',
        ],
        required: true,
      },
      {
        id: 'outils_principaux',
        label: 'Outils et systèmes principaux',
        type: 'multiselect',
        options: [
          'Plateformes de gestion de portefeuilles',
          'Outils de planification financière',
          'CRM pour suivi client',
          'Portails clients (web / mobile)',
          'Outils de rapports consolidés multi-institutions',
        ],
        required: true,
      },
      {
        id: 'contraintes_reglementaires',
        label: 'Contraintes réglementaires / conformité',
        type: 'multiselect',
        options: [
          'AMF / Organismes réglementaires',
          'Connaissance du client (KYC)',
          'Convenance des placements',
          'Protection des renseignements personnels',
        ],
        required: true,
      },
      {
        id: 'donnees_critiques',
        label: 'Données critiques',
        type: 'multiselect',
        options: [
          'Profils clients (objectifs, horizon, tolérance au risque)',
          'Données de portefeuilles (positions, rendement)',
          'Données fiscales et successorales',
          'Documentation légale (mandats, fiducies, testaments, etc.)',
        ],
        required: true,
      },
      {
        id: 'dependances_ti',
        label: 'Dépendances TI majeures',
        type: 'multiselect',
        options: [
          'Qualité et fiabilité des données de portefeuilles',
          'Intégration multi-custodians / multi-institutions',
          'Portails clients sécurisés et conviviaux',
          'Outils de simulation et de scénarios financiers',
        ],
        required: true,
      },
      {
        id: 'temps_arret_max',
        label: 'Temps d’arrêt maximal acceptable des systèmes critiques',
        type: 'select',
        options: ['30 minutes', '2 heures', '1 jour'],
        required: true,
      },
      {
        id: 'enjeux_principaux',
        label: 'Top enjeux métier identifiés',
        type: 'multiselect',
        options: [
          'Consolidation des données clients dispersées',
          'Qualité des rapports pour clients sophistiqués',
          'Sécurité et confidentialité élevées',
          'Intégration entre CRM, plateformes de placements et outils de planification',
        ],
        required: true,
        maxSelections: 5,
      },
      {
        id: 'remarques',
        label: 'Remarques / contexte supplémentaire',
        type: 'textarea',
        required: false,
      },
    ],
  },
];
